import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Stack,
  Paper,
  Title,
  Text,
  Group,
  Badge,
  Button,
  Grid,
  Center,
  Divider,
  TextInput,
  Select,
  Box,
  Flex,
  LoadingOverlay,
  Alert,
  SegmentedControl,
  ActionIcon,
  Tooltip,
  ScrollArea,
  Modal,
  Collapse,
  NumberInput,
  Switch,
  Popover
} from '@mantine/core';
import {
  IconCalendar,
  IconArrowLeft,
  IconSearch,
  IconDeviceFloppy,
  IconAlertCircle,
  IconCalendarEvent,
  IconGripVertical,
  IconTrash,
  IconPlus,
  IconExclamationMark,
  IconArrowRight,
  IconEdit,
  IconHistory,
  IconChevronDown,
  IconLock,
  IconUsers
} from '@tabler/icons-react';
import { DatePicker, DateInput, TimeInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { notifications } from '@mantine/notifications';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

import { pathnames } from 'routes/yearlyCalendar';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import {
  yearlyCalendarSchedulePlanService,
  UpdateSchedulePlanDto,
  YearlyCalendarCourse
} from 'services/yearlyCalendar/schedulePlanService';
import { courseService } from 'services/lcms/course.service';
import type { MantineSize, TextInputProps } from '@mantine/core';
import ScheduleImportExportModal from './components/ScheduleImportExportModal';
import { SchedulePlanEditSkeleton } from './components/SchedulePlanEditSkeleton';

dayjs.extend(customParseFormat);

interface ModuleBreakdown {
  TrModuleID?: string;
  moduleType: string;
  moduleStart: string;
  moduleEnd: string;
}

interface CourseInstance extends YearlyCalendarCourse {
  instanceId: string;
  hasConflict?: boolean;
  conflictWith?: string[];
  modeOfDelivery?: string;
  schoolName?: string;
  listModule?: ModuleBreakdown[];
  isCEAReady?: boolean;
  statusCEA?: number | null;
  reSchedule?: boolean;
  historyTransaksi?: Array<{
    startDate: string;
    endDate: string;
  }>;
  facilitators?: Array<{
    name: string;
    email: string;
    type: string;
    binusianID: string;
    approvalLevel: string | null;
    linkURL: string;
  }>;
}

interface AvailableCourse {
  msLCMSCourseID: string;
  courseName: string;
  courseCode: string;
  learningHours: number;
  deliveryType?: string;
  description?: string;
  modeOfDelivery?: string;
  schoolName?: string;
  facilitators?: Array<{
    name: string;
    email: string;
    type: string;
    binusianID: string;
    approvalLevel: string | null;
    linkURL: string;
  }>;
}

interface ConflictInfo {
  instanceId: string;
  conflictsWith: string[];
  overlapHours: number;
}

type ViewMode = 'table' | 'calendar';
type CalendarViewType = 'month' | 'week' | 'day';

const convertInstancesToUpdateDto = (
  planningId: string,
  courseInstances: CourseInstance[],
  assessmentStart: Date | null,
  assessmentEnd: Date | null
): UpdateSchedulePlanDto => {
  // Filter out batches that don't have both start and end dates
  const scheduledInstances = courseInstances.filter(
    (instance) => instance.planningStart && instance.planningEnd
  );

  const payload: any = {
    msLCMSPlanningID: planningId,
    courses: scheduledInstances.map((instance) => {
      const item: any = {
        courseID: instance.msLCMSCourseID.toUpperCase(),
        // Convert local time back to UTC for backend
        planningStart: instance.planningStart
          ? convertLocalToUTC(new Date(instance.planningStart))
          : undefined,
        planningEnd: instance.planningEnd
          ? convertLocalToUTC(new Date(instance.planningEnd))
          : undefined,
        isCEAReady: instance.isCEAReady || false,
        reSchedule: instance.reSchedule ? 1 : 0,
        modeOfDelivery: instance.modeOfDelivery || undefined,
        // Always include trLCMSPlanningCoursesID: null for new batches, actual ID for existing batches
        trLCMSPlanningCoursesID: instance.trLCMSPlanningCoursesID || null
      };

      if (typeof (instance as any).budget === 'number') {
        item.budget = (instance as any).budget;
      }

      if (instance.listModule && instance.listModule.length > 0) {
        item.listModule = instance.listModule.map((module) => ({
          TrModuleID: module.TrModuleID,
          moduleType: module.moduleType,
          moduleStart: module.moduleStart,
          moduleEnd: module.moduleEnd
        }));
      }

      return item;
    })
  };

  // Always include assessment dates, even if null - convert to string format
  if (assessmentStart) {
    payload.scheduleAssessmentStart = convertLocalToUTC(assessmentStart);
  }
  if (assessmentEnd) {
    payload.scheduleAssessmentEnd = convertLocalToUTC(assessmentEnd);
  }

  return payload;
};

const convertUTCToLocal = (utcDateString: string | null): Date | null => {
  if (!utcDateString) return null;

  const utcDate = new Date(utcDateString);

  if (isNaN(utcDate.getTime())) return null;

  return utcDate;
};

const convertLocalToUTC = (localDate: Date | null): string => {
  if (!localDate) return '';

  return localDate.toISOString();
};

// Helper function to get sorted sessions with session labels
const getSortedSessionsWithLabels = (listModule: ModuleBreakdown[] | undefined) => {
  if (!listModule || listModule.length === 0) return [];

  // Create a copy and sort by moduleStart date (ASC)
  const sortedModules = [...listModule].sort((a, b) => {
    if (!a.moduleStart && !b.moduleStart) return 0;
    if (!a.moduleStart) return 1;
    if (!b.moduleStart) return -1;

    const dateA = new Date(a.moduleStart);
    const dateB = new Date(b.moduleStart);
    return dateA.getTime() - dateB.getTime();
  });

  // Add session labels and original index for tracking
  return sortedModules.map((module, index) => ({
    ...module,
    sessionLabel: `Session ${index + 1}`,
    originalIndex: listModule.indexOf(module)
  }));
};

const formatDateTimeForDisplay = (
  dateString: string | null,
  options: Intl.DateTimeFormatOptions = {}
) => {
  if (!dateString) return '';

  const localDate = convertUTCToLocal(dateString);
  if (!localDate) return '';

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...options
  };

  return localDate.toLocaleDateString('en-US', defaultOptions);
};

// eslint-disable-next-line no-unused-vars
const detectConflicts = (courseInstances: CourseInstance[]): ConflictInfo[] => {
  const conflicts: ConflictInfo[] = [];
  const instancesWithDates = courseInstances.filter(
    (instance) => instance.planningStart && instance.planningEnd
  );

  instancesWithDates.forEach((instance, index) => {
    const conflictsWith: string[] = [];
    const startDate = new Date(instance.planningStart);
    const endDate = new Date(instance.planningEnd);

    instancesWithDates.forEach((otherInstance, otherIndex) => {
      if (index !== otherIndex && instance.msLCMSCourseID === otherInstance.msLCMSCourseID) {
        const otherStart = new Date(otherInstance.planningStart);
        const otherEnd = new Date(otherInstance.planningEnd);

        if (startDate < otherEnd && otherStart < endDate) {
          conflictsWith.push(otherInstance.instanceId);
        }
      }
    });

    if (conflictsWith.length > 0) {
      conflicts.push({
        instanceId: instance.instanceId,
        conflictsWith,
        overlapHours: 0
      });
    }
  });

  return conflicts;
};

const generateInstanceId = (courseId: string): string => {
  return `${courseId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Helper function to ensure date string has UTC timezone suffix
const ensureUTCFormat = (dateString: string): string => {
  if (!dateString) return dateString;
  // If the date string doesn't end with 'Z' and looks like ISO format, add 'Z'
  if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/) && !dateString.endsWith('Z')) {
    return dateString + 'Z';
  }
  return dateString;
};

const getCourseColor = (courseId: string): string => {
  const colors = [
    '#228be6',
    '#fa5252',
    '#40c057',
    '#fd7e14',
    '#845ef7',
    '#15aabf',
    '#e599f7',
    '#51cf66',
    '#ff8cc8',
    '#74c0fc',
    '#ffd43b',
    '#69db7c',
    '#ff6b6b',
    '#4c6ef5',
    '#9775fa',
    '#22b8cf',
    '#f76707',
    '#495057',
    '#868e96',
    '#adb5bd'
  ];

  let hash = 0;
  for (let i = 0; i < courseId.length; i++) {
    hash = courseId.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

type TimeInputExternalProps = Partial<
  Omit<React.ComponentProps<typeof TimeInput>, 'value' | 'defaultValue' | 'onChange'>
> & {
  onChange?: React.ComponentProps<typeof TimeInput>['onChange'];
  onBlur?: React.ComponentProps<typeof TimeInput>['onBlur'];
  onKeyDown?: React.ComponentProps<typeof TimeInput>['onKeyDown'];
};

interface ManualDateTimePickerProps
  extends Omit<TextInputProps, 'value' | 'onChange' | 'defaultValue' | 'type' | 'size'> {
  value: Date | null;
  onChange: (_value: Date | null) => void;
  valueFormat?: string;
  timeInputProps?: TimeInputExternalProps;
  minDate?: Date;
  maxDate?: Date;
  size?: MantineSize;
}

// Custom DateTimePicker implementation that supports manual typing and picker selection.
const DateTimePicker: React.FC<ManualDateTimePickerProps> = ({
  value,
  onChange,
  valueFormat = 'DD/MM/YYYY HH:mm',
  timeInputProps,
  minDate,
  maxDate,
  styles,
  disabled,
  leftSection,
  size = 'sm',
  rightSection: externalRightSection,
  onBlur: externalOnBlur,
  onKeyDown: externalOnKeyDown,
  ...textInputProps
}) => {
  const {
    onChange: timeOnChange,
    onBlur: timeOnBlur,
    onKeyDown: timeOnKeyDown,
    withSeconds,
    ...restTimeInputProps
  } = timeInputProps || {};
  const timeFormat = withSeconds ? 'HH:mm:ss' : 'HH:mm';
  const [opened, handlers] = useDisclosure(false);
  const [inputValue, setInputValue] = useState<string>(
    value ? dayjs(value).format(valueFormat) : ''
  );
  const [datePart, setDatePart] = useState<Date | null>(value ? new Date(value) : null);
  const [timeValue, setTimeValue] = useState<string>(value ? dayjs(value).format(timeFormat) : '');

  useEffect(() => {
    setInputValue(value ? dayjs(value).format(valueFormat) : '');
    setDatePart(value ? new Date(value) : null);
    setTimeValue(value ? dayjs(value).format(timeFormat) : '');
  }, [value, valueFormat, timeFormat]);

  const isWithinBounds = useCallback(
    (date: Date) => {
      if (minDate && date.getTime() < minDate.getTime()) return false;
      if (maxDate && date.getTime() > maxDate.getTime()) return false;
      return true;
    },
    [minDate, maxDate]
  );

  const parseTimeString = (val: string): [number, number, number] => {
    if (!val) return [0, 0, 0];
    const parts = val.split(':').map((part) => Number(part));
    const [hours = 0, minutes = 0, seconds = 0] = parts;
    return [
      Number.isFinite(hours) ? hours : 0,
      Number.isFinite(minutes) ? minutes : 0,
      Number.isFinite(seconds) ? seconds : 0
    ];
  };

  const revertToCurrentValue = () => {
    setInputValue(value ? dayjs(value).format(valueFormat) : '');
    setDatePart(value ? new Date(value) : null);
    setTimeValue(value ? dayjs(value).format(timeFormat) : '');
  };

  const commitValue = (next: Date | null) => {
    if (next && !isWithinBounds(next)) {
      revertToCurrentValue();
      return;
    }
    onChange(next);
    setInputValue(next ? dayjs(next).format(valueFormat) : '');
    setDatePart(next ? new Date(next) : null);
    setTimeValue(next ? dayjs(next).format(timeFormat) : '');
  };

  const processManualValue = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      commitValue(null);
      return;
    }
    const parsed = dayjs(trimmed, valueFormat, true);
    if (parsed.isValid()) {
      commitValue(parsed.toDate());
    } else {
      revertToCurrentValue();
    }
  };

  const handleManualBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    externalOnBlur?.(event);
    processManualValue(event.currentTarget.value);
  };

  const handleManualChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.currentTarget.value);
  };

  const handleManualKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    externalOnKeyDown?.(event);
    if (event.key === 'Enter') {
      event.preventDefault();
      processManualValue(event.currentTarget.value);
      handlers.close();
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (!date) {
      commitValue(null);
      return;
    }
    const [hours, minutes, seconds] = parseTimeString(timeValue);
    const nextMoment = dayjs(date);
    nextMoment.hour(hours);
    nextMoment.minute(minutes);
    nextMoment.second(seconds);
    nextMoment.millisecond(0);
    commitValue(nextMoment.toDate());
  };

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    timeOnChange?.(event);
    const val = event.currentTarget.value;
    setTimeValue(val);
    if (!datePart) {
      return;
    }
    const [hours, minutes, seconds] = parseTimeString(val);
    if ([hours, minutes, seconds].some((num) => Number.isNaN(num))) {
      return;
    }
    const nextMoment = dayjs(datePart);
    nextMoment.hour(hours);
    nextMoment.minute(minutes);
    nextMoment.second(seconds);
    nextMoment.millisecond(0);
    commitValue(nextMoment.toDate());
  };

  const handleTimeBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    timeOnBlur?.(event);
    if (!datePart) return;
    const [hours, minutes, seconds] = parseTimeString(event.currentTarget.value);
    const nextMoment = dayjs(datePart);
    nextMoment.hour(hours);
    nextMoment.minute(minutes);
    nextMoment.second(seconds);
    nextMoment.millisecond(0);
    commitValue(nextMoment.toDate());
  };

  const handleTimeKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    timeOnKeyDown?.(event);
    if (event.key === 'Enter') {
      event.preventDefault();
      handlers.close();
    }
  };

  const calendarButton = (
    <ActionIcon
      variant='subtle'
      color='gray'
      size={size === 'xs' ? 'sm' : 'md'}
      onClick={() => (opened ? handlers.close() : handlers.open())}
      disabled={disabled}
      aria-label='Open date and time picker'
    >
      <IconCalendar size={16} />
    </ActionIcon>
  );

  const inputStyles =
    typeof styles === 'function'
      ? styles
      : {
          ...(styles || {}),
          input: {
            ...((styles as Record<string, any>)?.input || {}),
            paddingRight: externalRightSection ? 64 : 40
          }
        };

  return (
    <Popover
      opened={opened && !disabled}
      onChange={(state) => {
        if (!state) {
          handlers.close();
        } else {
          handlers.open();
        }
      }}
      disabled={disabled}
      withinPortal
      trapFocus
      position='bottom-start'
      shadow='md'
      radius='md'
    >
      <Popover.Target>
        <TextInput
          {...textInputProps}
          value={inputValue}
          onChange={handleManualChange}
          onBlur={handleManualBlur}
          onKeyDown={handleManualKeyDown}
          leftSection={leftSection}
          disabled={disabled}
          size={size}
          styles={inputStyles}
          rightSection={
            externalRightSection ? (
              <Group gap={4} wrap='nowrap'>
                {externalRightSection}
                {calendarButton}
              </Group>
            ) : (
              calendarButton
            )
          }
        />
      </Popover.Target>
      <Popover.Dropdown p='sm'>
        <Stack gap='sm'>
          <DatePicker
            value={datePart}
            onChange={handleDateChange}
            minDate={minDate}
            maxDate={maxDate}
            size={size}
          />
          <TimeInput
            value={timeValue}
            withSeconds={withSeconds}
            onChange={handleTimeChange}
            onBlur={handleTimeBlur}
            onKeyDown={handleTimeKeyDown}
            disabled={disabled}
            size={size}
            {...restTimeInputProps}
          />
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

/* eslint-disable */
interface CalendarScheduleViewProps {
  courseInstances: CourseInstance[];
  onDateChange: (instanceId: string, type: 'start' | 'end', date: Date | null) => void;
  onRemoveInstance: (instanceId: string) => void;
  onUpdateInstance: (instanceId: string, updates: Partial<CourseInstance>) => void;
  conflicts: ConflictInfo[];
  // Module and CEA handlers
  onCEAReadyToggle: (instanceId: string, checked: boolean) => void;
  onAddModule: (instanceId: string) => void;
  onRemoveModule: (instanceId: string, moduleIndex: number) => void;
  onModuleChange: (
    instanceId: string,
    moduleIndex: number,
    field: keyof ModuleBreakdown,
    value: string
  ) => void;
  onModuleDateChange: (
    instanceId: string,
    moduleIndex: number,
    field: 'moduleStart' | 'moduleEnd',
    date: Date | null
  ) => void;
  modeOfDeliveryOptions: { id: number; option: string }[];
  loadingModeOfDelivery: boolean;
}
/* eslint-enable */

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  courseInstance: CourseInstance;
  color: string;
  hasConflict: boolean;
}

interface RibbonEvent {
  event: CalendarEvent;
  startCol: number;
  endCol: number;
  row: number;
  layer: number;
  isMultiWeek?: boolean;
  weekStartCol?: number;
  weekEndCol?: number;
}

const CalendarScheduleView: React.FC<CalendarScheduleViewProps> = ({
  courseInstances,
  onDateChange,
  onRemoveInstance,
  onUpdateInstance,
  conflicts,
  onCEAReadyToggle,
  onAddModule,
  onRemoveModule,
  onModuleChange,
  onModuleDateChange,
  modeOfDeliveryOptions,
  loadingModeOfDelivery
}) => {
  const [calendarView, setCalendarView] = useState<CalendarViewType>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [eventModalOpened, setEventModalOpened] = useState(false);
  const [draggedEventId, setDraggedEventId] = useState<string | null>(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [, setResizing] = useState<{
    eventId: string;
    side: 'left' | 'right';
    startX: number;
    originalStart: Date;
    originalEnd: Date;
  } | null>(null);

  const events = useMemo(() => {
    return courseInstances
      .filter((instance) => instance.planningStart && instance.planningEnd)
      .map((instance): CalendarEvent => {
        const hasConflict = conflicts.some(
          (conflict) => conflict.instanceId === instance.instanceId
        );
        const baseColor = getCourseColor(instance.msLCMSCourseID);

        // Dates are already in local time from the fetching process
        const startDate = instance.planningStart ? new Date(instance.planningStart) : null;
        const endDate = instance.planningEnd ? new Date(instance.planningEnd) : null;

        return {
          id: instance.instanceId,
          title: instance.courseName,
          start: startDate || new Date(),
          end: endDate || new Date(),
          courseInstance: instance,
          color: hasConflict ? '#ffa502' : baseColor,
          hasConflict
        };
      });
  }, [courseInstances, conflicts]);

  // Sync selectedEvent with updated courseInstances
  useEffect(() => {
    if (selectedEvent) {
      const updatedInstance = courseInstances.find(
        (instance) => instance.instanceId === selectedEvent.courseInstance.instanceId
      );
      if (updatedInstance) {
        const hasConflict = conflicts.some(
          (conflict) => conflict.instanceId === updatedInstance.instanceId
        );
        const baseColor = getCourseColor(updatedInstance.msLCMSCourseID);
        const startDate = updatedInstance.planningStart
          ? new Date(updatedInstance.planningStart)
          : null;
        const endDate = updatedInstance.planningEnd ? new Date(updatedInstance.planningEnd) : null;

        setSelectedEvent({
          ...selectedEvent,
          courseInstance: updatedInstance,
          color: hasConflict ? '#ffa502' : baseColor,
          hasConflict,
          start: startDate || new Date(),
          end: endDate || new Date()
        });
      }
    }
  }, [courseInstances, conflicts, selectedEvent?.courseInstance.instanceId]);

  const getCalendarDays = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (calendarView === 'month') {
      const firstDay = new Date(year, month, 1);
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());

      const days = [];
      const current = new Date(startDate);

      for (let i = 0; i < 42; i++) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }

      return days;
    } else if (calendarView === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

      const days = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(startOfWeek);
        day.setDate(startOfWeek.getDate() + i);
        days.push(day);
      }

      return days;
    } else {
      return [new Date(currentDate)];
    }
  }, [currentDate, calendarView]);

  const calendarDays = getCalendarDays();

  // Get events for a specific day
  const getEventsForDay = (day: Date) => {
    return events.filter((event) => {
      const dayStart = new Date(day);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(day);
      dayEnd.setHours(23, 59, 59, 999);

      return event.start <= dayEnd && event.end >= dayStart;
    });
  };

  // Check if event spans multiple days
  const isMultiDayEvent = (event: CalendarEvent) => {
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return endDate.getTime() > startDate.getTime();
  };

  // Calculate ribbon positions for multi-day events
  const calculateRibbonEvents = useMemo((): RibbonEvent[] => {
    if (calendarView !== 'month') return [];

    const ribbons: RibbonEvent[] = [];
    const ribbonKeys = new Set<string>();

    const toStartOfDay = (value: Date) => {
      const date = new Date(value);
      date.setHours(0, 0, 0, 0);
      return date;
    };

    // Group calendar days into weeks
    const weeks: Date[][] = [];
    let currentWeek: Date[] = [];

    calendarDays.forEach((day, index) => {
      currentWeek.push(day);
      if ((index + 1) % 7 === 0 || index === calendarDays.length - 1) {
        weeks.push([...currentWeek]);
        currentWeek = [];
      }
    });

    events.forEach((event) => {
      const eventStart = toStartOfDay(event.start);
      const eventEnd = toStartOfDay(event.end);

      weeks.forEach((week, weekIndex) => {
        const weekStart = new Date(week[0]);
        const weekEnd = new Date(week[6]);
        weekStart.setHours(0, 0, 0, 0);
        weekEnd.setHours(23, 59, 59, 999);

        if (event.start <= weekEnd && event.end >= weekStart) {
          let startCol = -1;
          let endCol = -1;

          const actualStart = eventStart.getTime() < weekStart.getTime() ? weekStart : eventStart;
          const actualEnd = eventEnd.getTime() > weekEnd.getTime() ? weekEnd : eventEnd;

          week.forEach((day, dayIndex) => {
            const dayStart = new Date(day);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(day);
            dayEnd.setHours(23, 59, 59, 999);

            if (actualStart <= dayEnd && actualEnd >= dayStart) {
              if (startCol === -1) {
                startCol = dayIndex;
              }
              endCol = dayIndex;
            }
          });

          if (startCol === -1 && eventStart.getTime() < weekStart.getTime()) {
            startCol = 0;
          }
          if (endCol === -1 && eventEnd.getTime() > weekEnd.getTime()) {
            endCol = 6;
          }

          if (startCol >= 0 && endCol >= 0 && startCol <= endCol) {
            const ribbonKey = `${event.id}-${weekIndex}-${startCol}-${endCol}`;
            if (ribbonKeys.has(ribbonKey)) return;

            ribbons.push({
              event,
              startCol,
              endCol,
              row: weekIndex,
              layer: 0,
              isMultiWeek:
                eventStart.getTime() < weekStart.getTime() ||
                eventEnd.getTime() > weekEnd.getTime(),
              weekStartCol: startCol,
              weekEndCol: endCol
            });
            ribbonKeys.add(ribbonKey);
          }
        }
      });
    });

    const eventLayerMap = new Map<string, number>();

    weeks.forEach((week, weekIndex) => {
      const weekRibbons = ribbons.filter((r) => r.row === weekIndex);
      weekRibbons.sort((a, b) => {
        if (a.startCol === b.startCol) return b.endCol - a.endCol;
        return a.startCol - b.startCol;
      });

      const layers: { start: number; end: number; eventId: string }[][] = [];

      weekRibbons.forEach((ribbon) => {
        let targetLayer = eventLayerMap.get(ribbon.event.id) ?? 0;

        while (true) {
          if (!layers[targetLayer]) layers[targetLayer] = [];

          const overlaps = layers[targetLayer].some(
            (placed) => !(ribbon.endCol < placed.start || ribbon.startCol > placed.end)
          );

          if (!overlaps) {
            layers[targetLayer].push({
              start: ribbon.startCol,
              end: ribbon.endCol,
              eventId: ribbon.event.id
            });
            ribbon.layer = targetLayer;
            eventLayerMap.set(ribbon.event.id, targetLayer);
            break;
          }

          targetLayer += 1;
        }
      });
    });

    const uniqueRibbons = ribbons.filter(
      (ribbon, index, self) =>
        self.findIndex(
          (r) =>
            r.event.id === ribbon.event.id &&
            r.row === ribbon.row &&
            r.startCol === ribbon.startCol &&
            r.endCol === ribbon.endCol
        ) === index
    );

    return uniqueRibbons;
  }, [events, calendarDays, calendarView]);

  // Calculate ribbon positions for week view
  const calculateWeekRibbonEvents = useMemo((): RibbonEvent[] => {
    if (calendarView !== 'week') return [];

    const ribbons: RibbonEvent[] = [];

    // Process only multi-day events for ribbons in week view
    events.forEach((event) => {
      // Skip single-day events as they will be rendered as individual items
      if (!isMultiDayEvent(event)) {
        return;
      }

      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      eventStart.setHours(0, 0, 0, 0);
      eventEnd.setHours(0, 0, 0, 0);

      const weekStart = new Date(calendarDays[0]);
      const weekEnd = new Date(calendarDays[6]);
      weekStart.setHours(0, 0, 0, 0);
      weekEnd.setHours(23, 59, 59, 999);

      // Only process if event overlaps with this week
      if (event.start <= weekEnd && event.end >= weekStart) {
        // Find start and end columns within this week
        let startCol = -1;
        let endCol = -1;

        calendarDays.forEach((day, dayIndex) => {
          const dayTime = new Date(day).setHours(0, 0, 0, 0);
          if (
            dayTime === eventStart.getTime() ||
            (dayTime > eventStart.getTime() && startCol === -1)
          ) {
            startCol = Math.max(0, dayIndex);
          }
          if (dayTime <= eventEnd.getTime()) {
            endCol = dayIndex;
          }
        });

        // Adjust for events that start before this week
        if (eventStart.getTime() < weekStart.getTime()) {
          startCol = 0;
        }

        // Adjust for events that end after this week
        if (eventEnd.getTime() > weekEnd.getTime()) {
          endCol = 6;
        }

        if (startCol >= 0 && endCol >= 0 && startCol <= endCol) {
          ribbons.push({
            event,
            startCol,
            endCol,
            row: 0, // Week view only has one row
            layer: 0, // Will be calculated later
            isMultiWeek:
              eventStart.getTime() < weekStart.getTime() || eventEnd.getTime() > weekEnd.getTime(),
            weekStartCol: startCol,
            weekEndCol: endCol
          });
        }
      }
    });

    // Calculate layers to avoid overlaps
    ribbons.sort((a, b) => a.event.start.getTime() - b.event.start.getTime());

    const layerTracker: boolean[][] = Array(7)
      .fill(null)
      .map(() => []);

    ribbons.forEach((ribbon) => {
      let layer = 0;
      let canPlace = false;

      while (!canPlace) {
        canPlace = true;
        for (let col = ribbon.startCol; col <= ribbon.endCol; col++) {
          if (layerTracker[col][layer]) {
            canPlace = false;
            break;
          }
        }
        if (!canPlace) layer++;
      }

      // Mark this layer as occupied for the duration of the event
      for (let col = ribbon.startCol; col <= ribbon.endCol; col++) {
        layerTracker[col][layer] = true;
      }

      ribbon.layer = layer;
    });

    return ribbons;
  }, [events, calendarDays, calendarView]);

  // Drop handlers for day cells
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetDate: Date) => {
    e.preventDefault();
    const eventId = e.dataTransfer.getData('text/plain');

    if (eventId && courseInstances) {
      const instanceToMove = courseInstances.find((instance) => instance.instanceId === eventId);
      if (instanceToMove) {
        const eventStart = new Date(instanceToMove.planningStart!);
        const eventEnd = new Date(instanceToMove.planningEnd!);

        // Calculate new dates while preserving original times
        const newStart = new Date(targetDate);
        newStart.setHours(
          eventStart.getHours(),
          eventStart.getMinutes(),
          eventStart.getSeconds(),
          eventStart.getMilliseconds()
        );

        const newEnd = new Date(targetDate);
        newEnd.setHours(
          eventEnd.getHours(),
          eventEnd.getMinutes(),
          eventEnd.getSeconds(),
          eventEnd.getMilliseconds()
        );

        // If it's a multi-day event, calculate the correct end date
        const originalDuration = eventEnd.getDate() - eventStart.getDate();
        if (originalDuration > 0) {
          newEnd.setDate(newEnd.getDate() + originalDuration);
        }

        // Update the course instance using the callback (store as local time)
        onUpdateInstance(eventId, {
          planningStart: newStart.toISOString(),
          planningEnd: newEnd.toISOString()
        });

        // Show notification
        notifications.show({
          title: 'Event Moved',
          message: `${instanceToMove.courseName} moved to ${newStart.toLocaleDateString()}`,
          color: 'blue'
        });
      }
    }

    setDraggedEventId(null);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const eventId = e.dataTransfer.getData('text/plain');
    setDraggedEventId(eventId);
  };

  // Resize handlers for ribbons
  const handleResizeStart = (eventId: string, side: 'left' | 'right', startX: number) => {
    const event = events?.find((e) => e.id === eventId);
    if (!event) return;

    const initialResizeState = {
      eventId,
      side,
      startX,
      originalStart: new Date(event.start),
      originalEnd: new Date(event.end)
    };

    setResizing(initialResizeState);

    // Add global mouse move and mouse up listeners
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const dayWidth = 200; // More accurate day width estimate
      const daysDelta = Math.round(deltaX / dayWidth);

      if (side === 'left') {
        // Resize from left (change start date)
        const newStart = new Date(initialResizeState.originalStart);
        newStart.setDate(newStart.getDate() + daysDelta);

        // Ensure start doesn't go past end
        if (newStart < initialResizeState.originalEnd) {
          // Update the event with new start date
          handleEventResize(eventId, newStart, initialResizeState.originalEnd);
        }
      } else {
        // Resize from right (change end date)
        const newEnd = new Date(initialResizeState.originalEnd);
        newEnd.setDate(newEnd.getDate() + daysDelta);

        // Ensure end doesn't go before start
        if (newEnd > initialResizeState.originalStart) {
          // Update the event with new end date
          handleEventResize(eventId, initialResizeState.originalStart, newEnd);
        }
      }
    };

    const handleMouseUp = () => {
      setResizing(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleEventResize = (eventId: string, newStart: Date, newEnd: Date) => {
    const event = events?.find((e) => e.id === eventId);
    if (!event || !onUpdateInstance) return;

    // Update the course instance with new dates (store as local time)
    onUpdateInstance(event.courseInstance.instanceId, {
      ...event.courseInstance,
      planningStart: newStart.toISOString(),
      planningEnd: newEnd.toISOString()
    });
  };

  // Navigation handlers
  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (calendarView === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (calendarView === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (calendarView === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (calendarView === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setHistoryExpanded(false); // Reset history expansion
    setEventModalOpened(true);
  };

  const formatDateHeader = () => {
    if (calendarView === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (calendarView === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  const renderEventItem = (event: CalendarEvent, day?: Date, isCompact: boolean = false) => {
    // Only render single-day events as individual items since multi-day events use ribbons
    return (
      <Box
        key={`${event.id}-${day?.getTime() || 'single'}`}
        p={isCompact ? 4 : 6}
        mb={2}
        style={{
          backgroundColor: event.color,
          color: 'white',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: isCompact ? 11 : 13,
          border: event.hasConflict ? '2px solid #ff6b35' : 'none',
          position: 'relative',
          minHeight: isCompact ? 20 : 28
        }}
        onClick={() => handleEventClick(event)}
      >
        <Group gap={4} wrap='nowrap'>
          {event.hasConflict && <IconExclamationMark size={12} style={{ color: '#fff' }} />}
          <Text size={isCompact ? 'xs' : 'sm'} fw={600} lineClamp={1} style={{ color: 'white' }}>
            {event.title}
          </Text>
        </Group>
        {!isCompact && (
          <Text size='xs' style={{ color: 'rgba(255,255,255,0.9)' }}>
            {event.courseInstance.courseCode}
          </Text>
        )}
      </Box>
    );
  };

  const renderMonthView = () => {
    const ribbonEvents = calculateRibbonEvents;
    const weeksCount = Math.ceil(calendarDays.length / 7);
    const maxLayersPerWeek = new Array(weeksCount).fill(0);

    // Calculate required height for each week based on ribbon layers
    ribbonEvents.forEach((ribbon) => {
      maxLayersPerWeek[ribbon.row] = Math.max(maxLayersPerWeek[ribbon.row], ribbon.layer + 1);
    });

    const baseCellHeight = 120;
    const ribbonLayerHeight = 22;
    const weekCellHeights = maxLayersPerWeek.map(
      (layers) => baseCellHeight + layers * ribbonLayerHeight
    );

    return (
      <Box>
        {/* Days of week header */}
        <Grid gutter={0} mb='sm'>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Grid.Col key={day} span={12 / 7}>
              <Text ta='center' fw={500} size='sm' c='dimmed'>
                {day}
              </Text>
            </Grid.Col>
          ))}
        </Grid>

        {/* Calendar grid with ribbons overlay */}
        <Box style={{ position: 'relative' }}>
          <Grid gutter={1}>
            {calendarDays.map((day, index) => {
              const dayEvents = getEventsForDay(day);
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              const isToday = day.toDateString() === new Date().toDateString();
              const weekIndex = Math.floor(index / 7);
              const cellHeight = weekCellHeights[weekIndex];

              // Month view uses ribbons for all events to align layering with the read-only view
              const singleDayEvents: CalendarEvent[] = [];

              return (
                <Grid.Col key={index} span={12 / 7}>
                  <Paper
                    p='xs'
                    h={cellHeight}
                    withBorder
                    style={{
                      backgroundColor: draggedEventId
                        ? '#e3f2fd'
                        : isCurrentMonth
                          ? 'white'
                          : '#f8f9fa',
                      borderColor: draggedEventId ? '#2196f3' : isToday ? '#228be6' : '#dee2e6',
                      borderWidth: draggedEventId ? 2 : isToday ? 2 : 1,
                      position: 'relative',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: cellHeight
                    }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, day)}
                    onDragEnter={handleDragEnter}
                  >
                    {/* Date header - fixed at top */}
                    <Box
                      style={{
                        height: 32,
                        paddingTop: 4,
                        paddingBottom: 4,
                        borderBottom: '1px solid #e9ecef',
                        flexShrink: 0
                      }}
                    >
                      <Group justify='space-between' h='100%'>
                        <Text
                          size='sm'
                          fw={isToday ? 600 : 400}
                          c={isCurrentMonth ? 'dark' : 'dimmed'}
                        >
                          {day.getDate()}
                        </Text>
                        {dayEvents.length > 0 && (
                          <Badge size='xs' variant='filled' color='blue'>
                            {dayEvents.length}
                          </Badge>
                        )}
                      </Group>
                    </Box>

                    {/* Scrollable content area for single-day events only */}
                    <Box
                      style={{
                        flex: 1,
                        overflowY: 'auto',
                        paddingTop: 4,
                        paddingBottom: 4
                      }}
                    >
                      <Stack gap={2}>
                        {/* Single day events only */}
                        {singleDayEvents.slice(0, 3).map((event) => (
                          <Box
                            key={event.id}
                            style={{
                              padding: '4px 6px',
                              backgroundColor: event.color,
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 500,
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setSelectedEvent(event);
                              setEventModalOpened(true);
                            }}
                          >
                            <Text size='xs' truncate style={{ color: 'white', fontSize: '11px' }}>
                              {event.title}
                            </Text>
                          </Box>
                        ))}

                        {singleDayEvents.length > 3 && (
                          <Text size='xs' c='dimmed' ta='center' style={{ padding: '2px 0' }}>
                            +{singleDayEvents.length - 3} more
                          </Text>
                        )}
                      </Stack>
                    </Box>
                  </Paper>
                </Grid.Col>
              );
            })}
          </Grid>

          {ribbonEvents.map((ribbon) => {
            const weekIndex = ribbon.row;
            const cellWidth = 'calc((100% - 6px) / 7)';
            const leftPosition = `calc(${ribbon.startCol} * (${cellWidth} + 1px))`;
            const ribbonWidth = `calc(${ribbon.endCol - ribbon.startCol + 1} * ${cellWidth} + ${ribbon.endCol - ribbon.startCol}px)`;
            const weekStartY = weekCellHeights.slice(0, weekIndex).reduce((a, b) => a + b, 0);
            const topPosition = weekStartY + 36 + ribbon.layer * ribbonLayerHeight;

            const borderRadius = (() => {
              if (ribbon.startCol === ribbon.endCol) {
                return '8px';
              } else {
                return '8px';
              }
            })();

            return (
              <Box
                key={`ribbon-overlay-${ribbon.event.id}-${ribbon.row}`}
                draggable={true}
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', ribbon.event.id);
                  e.dataTransfer.effectAllowed = 'move';
                  setDraggedEventId(ribbon.event.id);
                  (e.currentTarget as HTMLElement).style.opacity = '0.5';
                }}
                onDragEnd={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '1';
                }}
                onClick={() => {
                  setSelectedEvent(ribbon.event);
                  setEventModalOpened(true);
                }}
                style={{
                  position: 'absolute',
                  left: leftPosition,
                  top: topPosition,
                  width: ribbonWidth,
                  height: ribbonLayerHeight - 2,
                  background: `linear-gradient(135deg, ${ribbon.event.color}, ${ribbon.event.color}dd)`,
                  color: 'white',
                  borderRadius,
                  cursor: 'move',
                  paddingLeft: 6,
                  paddingRight: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 500,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  border: ribbon.event.hasConflict ? '2px solid #ff6b35' : 'none',
                  transition: 'all 0.2s ease',
                  zIndex: 100 + ribbon.layer,
                  pointerEvents: 'auto'
                }}
              >
                <Text
                  size='xs'
                  style={{
                    color: 'white',
                    fontSize: '11px',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%'
                  }}
                >
                  {ribbon.event.title}
                </Text>

                {/* Left resize handle with grip indicator */}
                <Box
                  className='left-resize-handle'
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: 8,
                    height: '100%',
                    cursor: 'ew-resize',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    borderRadius: '3px 0 0 3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.3,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.3';
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.9)';
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleResizeStart(ribbon.event.id, 'left', e.clientX);
                  }}
                >
                  <div
                    style={{
                      width: '3px',
                      height: '12px',
                      background:
                        'repeating-linear-gradient(to bottom, transparent 0px, transparent 1px, rgba(100,100,100,0.6) 1px, rgba(100,100,100,0.6) 2px)'
                    }}
                  />
                </Box>

                {/* Drag indicator in the center */}
                <Box
                  className='drag-handle'
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 20,
                    height: 8,
                    cursor: 'move',
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                    zIndex: 1
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0';
                  }}
                >
                  <div
                    style={{
                      width: '3px',
                      height: '3px',
                      backgroundColor: 'rgba(100,100,100,0.8)',
                      borderRadius: '50%',
                      margin: '0 1px'
                    }}
                  />
                  <div
                    style={{
                      width: '3px',
                      height: '3px',
                      backgroundColor: 'rgba(100,100,100,0.8)',
                      borderRadius: '50%',
                      margin: '0 1px'
                    }}
                  />
                  <div
                    style={{
                      width: '3px',
                      height: '3px',
                      backgroundColor: 'rgba(100,100,100,0.8)',
                      borderRadius: '50%',
                      margin: '0 1px'
                    }}
                  />
                </Box>

                {/* Right resize handle with grip indicator */}
                <Box
                  className='right-resize-handle'
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: 8,
                    height: '100%',
                    cursor: 'ew-resize',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    borderRadius: '0 3px 3px 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.3,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.3';
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.9)';
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleResizeStart(ribbon.event.id, 'right', e.clientX);
                  }}
                >
                  <div
                    style={{
                      width: '3px',
                      height: '12px',
                      background:
                        'repeating-linear-gradient(to bottom, transparent 0px, transparent 1px, rgba(100,100,100,0.6) 1px, rgba(100,100,100,0.6) 2px)'
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderWeekView = () => {
    const weekRibbonEvents = calculateWeekRibbonEvents;

    return (
      <Box>
        {/* Days of week header */}
        <Grid gutter={0} mb='sm'>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Grid.Col key={day} span={12 / 7}>
              <Text ta='center' fw={500} size='sm' c='dimmed'>
                {day}
              </Text>
            </Grid.Col>
          ))}
        </Grid>

        {/* Week calendar grid with ribbons overlay */}
        <Box style={{ position: 'relative' }}>
          <Grid gutter={1}>
            {calendarDays.map((day, index) => {
              const dayEvents = getEventsForDay(day);
              const isToday = day.toDateString() === new Date().toDateString();

              // Filter out multi-day events that will be shown as ribbons
              const singleDayEvents = dayEvents.filter((event) => !isMultiDayEvent(event));

              return (
                <Grid.Col key={index} span={12 / 7}>
                  <Paper
                    p='md'
                    withBorder
                    style={{
                      minHeight: 300,
                      backgroundColor: draggedEventId ? '#e3f2fd' : 'white',
                      borderColor: draggedEventId ? '#2196f3' : isToday ? '#228be6' : '#dee2e6',
                      borderWidth: draggedEventId ? 2 : isToday ? 2 : 1,
                      position: 'relative',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, day)}
                    onDragEnter={handleDragEnter}
                  >
                    {/* Date header */}
                    <Group justify='space-between' mb='md' style={{ flexShrink: 0 }}>
                      <Box>
                        <Text fw={500} size='sm'>
                          {day.toLocaleDateString('en-US', { weekday: 'short' })}
                        </Text>
                        <Text size='lg' fw={isToday ? 600 : 400} c={isToday ? 'blue' : 'dark'}>
                          {day.getDate()}
                        </Text>
                      </Box>
                      {dayEvents.length > 0 && (
                        <Badge size='sm' variant='filled' color='blue'>
                          {dayEvents.length}
                        </Badge>
                      )}
                    </Group>

                    {/* Content area for single-day events */}
                    <Box style={{ flex: 1, paddingTop: 30 }}>
                      <Stack gap='xs'>
                        {singleDayEvents.map((event) => renderEventItem(event, day))}
                      </Stack>
                    </Box>
                  </Paper>
                </Grid.Col>
              );
            })}
          </Grid>

          {/* Week view ribbons overlay */}
          {weekRibbonEvents.map((ribbon) => {
            const cellWidth = 'calc((100% - 6px) / 7)';
            const leftPosition = `calc(${ribbon.startCol} * (${cellWidth} + 1px))`;
            const ribbonWidth = `calc(${ribbon.endCol - ribbon.startCol + 1} * ${cellWidth} + ${ribbon.endCol - ribbon.startCol}px)`;
            const topPosition = 60 + ribbon.layer * 25; // Position below the header

            const borderRadius = (() => {
              if (ribbon.startCol === ribbon.endCol) {
                return '8px';
              } else {
                return '8px';
              }
            })();

            return (
              <Box
                key={`week-ribbon-${ribbon.event.id}`}
                draggable={true}
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', ribbon.event.id);
                  e.dataTransfer.effectAllowed = 'move';
                  setDraggedEventId(ribbon.event.id);
                  (e.currentTarget as HTMLElement).style.opacity = '0.5';
                }}
                onDragEnd={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '1';
                }}
                onClick={() => {
                  setSelectedEvent(ribbon.event);
                  setEventModalOpened(true);
                }}
                style={{
                  position: 'absolute',
                  left: leftPosition,
                  top: topPosition,
                  width: ribbonWidth,
                  height: 22,
                  background: `linear-gradient(135deg, ${ribbon.event.color}, ${ribbon.event.color}dd)`,
                  color: 'white',
                  borderRadius,
                  cursor: 'move',
                  paddingLeft: 8,
                  paddingRight: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 500,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  border: ribbon.event.hasConflict ? '2px solid #ff6b35' : 'none',
                  transition: 'all 0.2s ease',
                  zIndex: 100 + ribbon.layer,
                  pointerEvents: 'auto'
                }}
              >
                <Text
                  size='xs'
                  style={{
                    color: 'white',
                    fontSize: '12px',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%'
                  }}
                >
                  {ribbon.event.title}
                </Text>

                {/* Left resize handle */}
                <Box
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: 8,
                    height: '100%',
                    cursor: 'ew-resize',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    borderRadius: '3px 0 0 3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.3,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.3';
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.9)';
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleResizeStart(ribbon.event.id, 'left', e.clientX);
                  }}
                >
                  <div
                    style={{
                      width: '3px',
                      height: '12px',
                      background:
                        'repeating-linear-gradient(to bottom, transparent 0px, transparent 1px, rgba(100,100,100,0.6) 1px, rgba(100,100,100,0.6) 2px)'
                    }}
                  />
                </Box>

                {/* Drag indicator */}
                <Box
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 20,
                    height: 8,
                    cursor: 'move',
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                    zIndex: 1
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0';
                  }}
                >
                  <div
                    style={{
                      width: '3px',
                      height: '3px',
                      backgroundColor: 'rgba(100,100,100,0.8)',
                      borderRadius: '50%',
                      margin: '0 1px'
                    }}
                  />
                  <div
                    style={{
                      width: '3px',
                      height: '3px',
                      backgroundColor: 'rgba(100,100,100,0.8)',
                      borderRadius: '50%',
                      margin: '0 1px'
                    }}
                  />
                  <div
                    style={{
                      width: '3px',
                      height: '3px',
                      backgroundColor: 'rgba(100,100,100,0.8)',
                      borderRadius: '50%',
                      margin: '0 1px'
                    }}
                  />
                </Box>

                {/* Right resize handle */}
                <Box
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: 8,
                    height: '100%',
                    cursor: 'ew-resize',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    borderRadius: '0 3px 3px 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.3,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.3';
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.9)';
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleResizeStart(ribbon.event.id, 'right', e.clientX);
                  }}
                >
                  <div
                    style={{
                      width: '3px',
                      height: '12px',
                      background:
                        'repeating-linear-gradient(to bottom, transparent 0px, transparent 1px, rgba(100,100,100,0.6) 1px, rgba(100,100,100,0.6) 2px)'
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderDayView = () => {
    const dayEvents = getEventsForDay(calendarDays[0]);
    const isToday = calendarDays[0].toDateString() === new Date().toDateString();

    return (
      <Paper p='lg' withBorder>
        <Group justify='space-between' mb='lg'>
          <Box>
            <Text size='xl' fw={600} c={isToday ? 'blue' : 'dark'}>
              {calendarDays[0].toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              })}
            </Text>
            <Text c='dimmed'>
              {dayEvents.length} course{dayEvents.length !== 1 ? 's' : ''} scheduled
            </Text>
          </Box>
        </Group>

        <Stack gap='md'>
          {dayEvents.length === 0 ? (
            <Box py={60} ta='center'>
              <IconCalendarEvent size={48} color='gray' />
              <Text c='dimmed' mt='md'>
                No courses scheduled for this day
              </Text>
            </Box>
          ) : (
            dayEvents.map((event) => (
              <Paper key={event.id} p='md' withBorder>
                <Group justify='space-between'>
                  <Box flex={1}>
                    <Group gap='sm'>
                      <Text fw={500} size='lg'>
                        {event.title}
                      </Text>
                      {event.hasConflict && (
                        <Tooltip label='Schedule conflict'>
                          <IconExclamationMark size={16} color='red' />
                        </Tooltip>
                      )}
                    </Group>
                    <Group gap='xs' mt='xs'>
                      <Badge variant='outline' size='sm'>
                        {event.courseInstance.courseCode}
                      </Badge>
                      <Badge variant='light' color='green' size='sm'>
                        {event.courseInstance.learningHours}h
                      </Badge>
                    </Group>
                    <Text size='sm' c='dimmed' mt='xs'>
                      {event.start.toLocaleDateString()} - {event.end.toLocaleDateString()}
                    </Text>
                  </Box>
                  <Group gap='xs'>
                    <ActionIcon variant='subtle' onClick={() => handleEventClick(event)}>
                      <IconCalendarEvent size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant='subtle'
                      color='red'
                      onClick={() => onRemoveInstance(event.courseInstance.instanceId)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Paper>
            ))
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <>
      <Paper p='md' radius='md'>
        {/* Calendar Header */}
        <Group justify='space-between' mb='lg'>
          <Group>
            <ActionIcon variant='subtle' onClick={handlePrevious}>
              <IconArrowLeft size={18} />
            </ActionIcon>
            <Text fw={600} size='lg' style={{ minWidth: 200, textAlign: 'center' }}>
              {formatDateHeader()}
            </Text>
            <ActionIcon variant='subtle' onClick={handleNext}>
              <IconArrowLeft size={18} style={{ transform: 'rotate(180deg)' }} />
            </ActionIcon>
          </Group>

          <SegmentedControl
            value={calendarView}
            onChange={(value) => setCalendarView(value as CalendarViewType)}
            data={[
              { label: 'Month', value: 'month' },
              { label: 'Week', value: 'week' },
              { label: 'Day', value: 'day' }
            ]}
          />
        </Group>

        {/* Calendar Content */}
        {calendarView === 'month' && renderMonthView()}
        {calendarView === 'week' && renderWeekView()}
        {calendarView === 'day' && renderDayView()}
      </Paper>
      {selectedEvent && (
        <Modal
          opened={eventModalOpened}
          onClose={() => {
            setEventModalOpened(false);
            setHistoryExpanded(false); // Reset history expansion
          }}
          title={null}
          size='lg'
          padding={0}
          radius='xl'
          centered
          withCloseButton={false}
          overlayProps={{
            backgroundOpacity: 0.55,
            blur: 8
          }}
          styles={{
            content: {
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: 0
            },
            body: {
              padding: 0
            }
          }}
        >
          <Box
            style={{
              background: 'white',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            {/* Header with Course Color */}
            <Box
              p='xl'
              style={{
                background: `linear-gradient(135deg, ${selectedEvent.color}, ${selectedEvent.color}dd)`,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Background Pattern */}
              <Box
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '200px',
                  height: '200px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  transform: 'translate(50%, -50%)'
                }}
              />

              <Group
                justify='space-between'
                align='flex-start'
                style={{ position: 'relative', zIndex: 1 }}
              >
                <Box>
                  <Group gap='sm' mb='xs'>
                    <Title order={2} c='white' fw={700}>
                      {selectedEvent.courseInstance.courseName}
                    </Title>
                    {selectedEvent.hasConflict && (
                      <Badge
                        color='orange'
                        variant='filled'
                        size='lg'
                        style={{
                          boxShadow: '0 4px 14px 0 rgba(255,152,0,0.39)'
                        }}
                      >
                        <Group gap={6}>
                          <IconExclamationMark size={14} />
                          Schedule Conflict
                        </Group>
                      </Badge>
                    )}
                  </Group>

                  <Group gap='md' mb='md'>
                    <Badge
                      variant='white'
                      size='lg'
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}
                    >
                      📚 {selectedEvent.courseInstance.courseCode}
                    </Badge>
                    <Badge
                      variant='white'
                      size='lg'
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}
                    >
                      ⏱️ {selectedEvent.courseInstance.learningHours} Hours
                    </Badge>
                    <Badge
                      variant='white'
                      size='lg'
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}
                    >
                      📋 {selectedEvent.courseInstance.deliveryType}
                    </Badge>
                  </Group>

                  <Text c='rgba(255,255,255,0.9)' size='sm' fw={500}>
                    Course Schedule Management
                  </Text>
                </Box>
              </Group>
            </Box>

            {/* Content */}
            <Box p='xl'>
              <Stack gap='xl'>
                {/* Current Schedule Info */}
                <Box>
                  <Group gap='sm' mb='md'>
                    <IconCalendarEvent size={20} color={selectedEvent.color} />
                    <Title order={4} c='dark'>
                      Current Schedule
                    </Title>
                  </Group>

                  <Paper
                    p='md'
                    radius='lg'
                    style={{
                      backgroundColor: '#f8f9ff',
                      border: `1px solid ${selectedEvent.color}30`
                    }}
                  >
                    <Group justify='space-between'>
                      <Box>
                        <Text size='sm' c='dimmed' fw={500}>
                          FROM
                        </Text>
                        <Text fw={600} c='dark'>
                          {selectedEvent.start.toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Text>
                        <Text size='sm' c='blue' fw={500}>
                          {selectedEvent.start.toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          })}
                        </Text>
                      </Box>
                      <Box ta='center'>
                        <IconArrowRight size={24} color='gray' />
                      </Box>
                      <Box>
                        <Text size='sm' c='dimmed' fw={500}>
                          TO
                        </Text>
                        <Text fw={600} c='dark'>
                          {selectedEvent.end.toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Text>
                        <Text size='sm' c='blue' fw={500}>
                          {selectedEvent.end.toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          })}
                        </Text>
                      </Box>
                    </Group>
                  </Paper>
                </Box>
                <Box>
                  <Group gap='sm' mb='md'>
                    <IconEdit size={20} color={selectedEvent.color} />
                    <Title order={4} c='dark'>
                      Update Schedule
                    </Title>
                  </Group>

                  <Grid>
                    <Grid.Col span={6}>
                      <DateTimePicker
                        label='Start Date & Time'
                        placeholder='DD/MM/YYYY HH:mm or pick date'
                        value={selectedEvent.start}
                        onChange={(date) => {
                          if (date) {
                            onDateChange(selectedEvent.courseInstance.instanceId, 'start', date);
                            setSelectedEvent((prev) => (prev ? { ...prev, start: date } : null));
                          }
                        }}
                        valueFormat='DD MMM YYYY HH:mm'
                        size='md'
                        leftSection={<IconCalendar size={16} />}
                        timeInputProps={{
                          withSeconds: false
                        }}
                        styles={{
                          label: { fontWeight: 600, color: '#374151' },
                          input: {
                            borderColor: '#e5e7eb',
                            borderRadius: '12px',
                            paddingLeft: '40px',
                            '&:focus': {
                              borderColor: selectedEvent.color,
                              boxShadow: `0 0 0 3px ${selectedEvent.color}20`
                            }
                          }
                        }}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <DateTimePicker
                        label='End Date & Time'
                        placeholder='DD/MM/YYYY HH:mm or pick date'
                        value={selectedEvent.end}
                        onChange={(date) => {
                          if (date) {
                            onDateChange(selectedEvent.courseInstance.instanceId, 'end', date);
                            setSelectedEvent((prev) => (prev ? { ...prev, end: date } : null));
                          }
                        }}
                        valueFormat='DD MMM YYYY HH:mm'
                        size='md'
                        leftSection={<IconCalendar size={16} />}
                        timeInputProps={{
                          withSeconds: false
                        }}
                        styles={{
                          label: { fontWeight: 600, color: '#374151' },
                          input: {
                            borderColor: '#e5e7eb',
                            borderRadius: '12px',
                            paddingLeft: '40px',
                            '&:focus': {
                              borderColor: selectedEvent.color,
                              boxShadow: `0 0 0 3px ${selectedEvent.color}20`
                            }
                          }
                        }}
                      />
                    </Grid.Col>
                  </Grid>
                </Box>
                {/* Session Breakdown Section */}
                {selectedEvent.courseInstance.deliveryType === 'Blended Learning' && (
                  <Box>
                    <Group gap='sm' mb='md'>
                      <IconCalendarEvent size={20} color={selectedEvent.color} />
                      <Title order={4} c='dark'>
                        Session Breakdown (Optional)
                      </Title>
                      <Button
                        variant='light'
                        size='xs'
                        color={selectedEvent.color}
                        leftSection={<IconPlus size={14} />}
                        onClick={() => onAddModule(selectedEvent.courseInstance.instanceId)}
                      >
                        Add Session
                      </Button>
                    </Group>

                    {selectedEvent.courseInstance.listModule &&
                    selectedEvent.courseInstance.listModule.length > 0 ? (
                      <Stack gap='sm'>
                        {getSortedSessionsWithLabels(selectedEvent.courseInstance.listModule).map(
                          (sessionData, _displayIndex) => {
                            const { sessionLabel, originalIndex, ...module } = sessionData;
                            return (
                              <Paper
                                key={originalIndex}
                                p='md'
                                radius='lg'
                                style={{
                                  backgroundColor: '#f8f9ff',
                                  border: `1px solid ${selectedEvent.color}30`,
                                  position: 'relative'
                                }}
                              >
                                <Group justify='space-between' align='center' mb='md'>
                                  <Badge
                                    size='lg'
                                    variant='filled'
                                    color={selectedEvent.color}
                                    style={{
                                      fontWeight: 600,
                                      fontSize: '13px'
                                    }}
                                  >
                                    {sessionLabel}
                                  </Badge>
                                  <Tooltip label='Remove Session'>
                                    <ActionIcon
                                      color='red'
                                      variant='light'
                                      size='md'
                                      onClick={() =>
                                        onRemoveModule(
                                          selectedEvent.courseInstance.instanceId,
                                          originalIndex
                                        )
                                      }
                                    >
                                      <IconTrash size={16} />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>

                                <Stack gap='md'>
                                  {/* Session Type Row */}
                                  <Box>
                                    <Select
                                      label='Session Type'
                                      placeholder='Select session type'
                                      data={modeOfDeliveryOptions.map((item) => item.option) || []}
                                      searchable
                                      disabled={loadingModeOfDelivery}
                                      value={module.moduleType}
                                      onChange={(value) =>
                                        onModuleChange(
                                          selectedEvent.courseInstance.instanceId,
                                          originalIndex,
                                          'moduleType',
                                          value || ''
                                        )
                                      }
                                      size='sm'
                                      styles={{
                                        label: { fontWeight: 600, color: '#374151' },
                                        input: {
                                          borderColor: '#e5e7eb',
                                          borderRadius: '12px'
                                        }
                                      }}
                                      leftSection={<IconCalendarEvent size={16} />}
                                    />
                                  </Box>

                                  {/* Date and Time Row */}
                                  <Grid gutter='md'>
                                    <Grid.Col span={6}>
                                      <Group gap='xs' align='flex-end' wrap='nowrap'>
                                        <Box style={{ flex: 2 }}>
                                          <DateInput
                                            label='Session Start Date'
                                            placeholder='DD/MM/YYYY'
                                            value={
                                              module.moduleStart && module.moduleStart !== ''
                                                ? (() => {
                                                    const date = new Date(module.moduleStart);
                                                    return isNaN(date.getTime()) ? null : date;
                                                  })()
                                                : null
                                            }
                                            onChange={(date) => {
                                              if (date) {
                                                const currentDate = module.moduleStart
                                                  ? new Date(module.moduleStart)
                                                  : new Date(date);
                                                currentDate.setFullYear(date.getFullYear());
                                                currentDate.setMonth(date.getMonth());
                                                currentDate.setDate(date.getDate());
                                                onModuleDateChange(
                                                  selectedEvent.courseInstance.instanceId,
                                                  originalIndex,
                                                  'moduleStart',
                                                  currentDate
                                                );
                                              }
                                            }}
                                            valueFormat='DD/MM/YYYY'
                                            size='sm'
                                            leftSection={<IconCalendar size={14} />}
                                            minDate={
                                              selectedEvent.courseInstance.planningStart
                                                ? (() => {
                                                    const date = new Date(
                                                      selectedEvent.courseInstance.planningStart
                                                    );
                                                    return isNaN(date.getTime()) ? undefined : date;
                                                  })()
                                                : undefined
                                            }
                                            maxDate={
                                              selectedEvent.courseInstance.planningEnd
                                                ? (() => {
                                                    const date = new Date(
                                                      selectedEvent.courseInstance.planningEnd
                                                    );
                                                    return isNaN(date.getTime()) ? undefined : date;
                                                  })()
                                                : undefined
                                            }
                                            styles={{
                                              label: {
                                                fontWeight: 600,
                                                color: '#374151',
                                                fontSize: '12px'
                                              },
                                              input: {
                                                borderColor: '#e5e7eb',
                                                borderRadius: '12px',
                                                fontSize: '13px'
                                              }
                                            }}
                                          />
                                        </Box>
                                        <Box style={{ flex: 1 }}>
                                          <TimeInput
                                            label='Time'
                                            placeholder='HH:mm'
                                            value={
                                              module.moduleStart
                                                ? dayjs(module.moduleStart).format('HH:mm')
                                                : ''
                                            }
                                            onChange={(event) => {
                                              const timeValue = event.currentTarget.value;
                                              if (timeValue && module.moduleStart) {
                                                const [hours, minutes] = timeValue
                                                  .split(':')
                                                  .map(Number);
                                                const currentDate = new Date(module.moduleStart);
                                                currentDate.setHours(hours, minutes, 0, 0);
                                                onModuleDateChange(
                                                  selectedEvent.courseInstance.instanceId,
                                                  originalIndex,
                                                  'moduleStart',
                                                  currentDate
                                                );
                                              }
                                            }}
                                            size='sm'
                                            styles={{
                                              label: {
                                                fontWeight: 600,
                                                color: '#374151',
                                                fontSize: '12px'
                                              },
                                              input: { fontSize: '13px' }
                                            }}
                                          />
                                        </Box>
                                      </Group>
                                    </Grid.Col>
                                    <Grid.Col span={6}>
                                      <Group gap='xs' align='flex-end' wrap='nowrap'>
                                        <Box style={{ flex: 2 }}>
                                          <DateInput
                                            label='Session End Date'
                                            placeholder='DD/MM/YYYY'
                                            value={
                                              module.moduleEnd && module.moduleEnd !== ''
                                                ? (() => {
                                                    const date = new Date(module.moduleEnd);
                                                    return isNaN(date.getTime()) ? null : date;
                                                  })()
                                                : null
                                            }
                                            onChange={(date) => {
                                              if (date) {
                                                const currentDate = module.moduleEnd
                                                  ? new Date(module.moduleEnd)
                                                  : new Date(date);
                                                currentDate.setFullYear(date.getFullYear());
                                                currentDate.setMonth(date.getMonth());
                                                currentDate.setDate(date.getDate());
                                                onModuleDateChange(
                                                  selectedEvent.courseInstance.instanceId,
                                                  originalIndex,
                                                  'moduleEnd',
                                                  currentDate
                                                );
                                              }
                                            }}
                                            valueFormat='DD/MM/YYYY'
                                            size='sm'
                                            leftSection={<IconCalendar size={14} />}
                                            minDate={
                                              selectedEvent.courseInstance.planningStart
                                                ? (() => {
                                                    const date = new Date(
                                                      selectedEvent.courseInstance.planningStart
                                                    );
                                                    return isNaN(date.getTime()) ? undefined : date;
                                                  })()
                                                : undefined
                                            }
                                            maxDate={
                                              selectedEvent.courseInstance.planningEnd
                                                ? (() => {
                                                    const date = new Date(
                                                      selectedEvent.courseInstance.planningEnd
                                                    );
                                                    return isNaN(date.getTime()) ? undefined : date;
                                                  })()
                                                : undefined
                                            }
                                            styles={{
                                              label: {
                                                fontWeight: 600,
                                                color: '#374151',
                                                fontSize: '12px'
                                              },
                                              input: {
                                                borderColor: '#e5e7eb',
                                                borderRadius: '12px',
                                                fontSize: '13px'
                                              }
                                            }}
                                          />
                                        </Box>
                                        <Box style={{ flex: 1 }}>
                                          <TimeInput
                                            label='Time'
                                            placeholder='HH:mm'
                                            value={
                                              module.moduleEnd
                                                ? dayjs(module.moduleEnd).format('HH:mm')
                                                : ''
                                            }
                                            onChange={(event) => {
                                              const timeValue = event.currentTarget.value;
                                              if (timeValue && module.moduleEnd) {
                                                const [hours, minutes] = timeValue
                                                  .split(':')
                                                  .map(Number);
                                                const currentDate = new Date(module.moduleEnd);
                                                currentDate.setHours(hours, minutes, 0, 0);
                                                onModuleDateChange(
                                                  selectedEvent.courseInstance.instanceId,
                                                  originalIndex,
                                                  'moduleEnd',
                                                  currentDate
                                                );
                                              }
                                            }}
                                            size='sm'
                                            styles={{
                                              label: {
                                                fontWeight: 600,
                                                color: '#374151',
                                                fontSize: '12px'
                                              },
                                              input: { fontSize: '13px' }
                                            }}
                                          />
                                        </Box>
                                      </Group>
                                    </Grid.Col>
                                  </Grid>
                                </Stack>
                              </Paper>
                            );
                          }
                        )}

                        <Text size='sm' c='dimmed' style={{ fontStyle: 'italic' }}>
                          💡 Session dates are constrained to be within the batch dates.
                        </Text>
                      </Stack>
                    ) : (
                      <Paper
                        p='md'
                        radius='lg'
                        style={{
                          backgroundColor: '#f8f9ff',
                          border: `1px solid ${selectedEvent.color}30`,
                          borderStyle: 'dashed'
                        }}
                      >
                        <Stack align='center' gap='sm'>
                          <IconPlus size={24} color={selectedEvent.color} opacity={0.6} />
                          <Text c='dimmed' size='sm' ta='center'>
                            No modules defined yet. Click "Add Session" to break down this course
                            into specific modules.
                          </Text>
                        </Stack>
                      </Paper>
                    )}
                  </Box>
                )}
                {/* CEA Ready Status */}
                <Box>
                  <Group gap='sm' mb='md'>
                    <IconCalendarEvent size={20} color={selectedEvent.color} />
                    <Title order={4} c='dark'>
                      CEA Ready Status
                    </Title>
                  </Group>

                  <Paper
                    p='md'
                    radius='lg'
                    style={{
                      backgroundColor: '#f8f9ff',
                      border: `1px solid ${selectedEvent.color}30`
                    }}
                  >
                    <Group justify='space-between' align='center'>
                      <Box>
                        <Text fw={600} size='sm' c='dark'>
                          CEA Ready
                        </Text>
                        <Text size='xs' c='dimmed'>
                          Mark this course as ready for CEA processing
                        </Text>
                      </Box>
                      <Switch
                        checked={selectedEvent.courseInstance.isCEAReady || false}
                        onChange={(event) => {
                          onCEAReadyToggle(
                            selectedEvent.courseInstance.instanceId,
                            event.currentTarget.checked
                          );
                          // Update the modal state
                          setSelectedEvent((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  courseInstance: {
                                    ...prev.courseInstance,
                                    isCEAReady: event.currentTarget.checked
                                  }
                                }
                              : null
                          );
                        }}
                        label={selectedEvent.courseInstance.isCEAReady ? 'Ready' : 'Not Ready'}
                        color='green'
                        size='md'
                      />
                    </Group>
                  </Paper>
                </Box>
                {/* History Transactions - Collapsible */}
                {selectedEvent.courseInstance.historyTransaksi &&
                  selectedEvent.courseInstance.historyTransaksi.length > 0 && (
                    <Box>
                      <Group
                        gap='sm'
                        mb='md'
                        style={{ cursor: 'pointer' }}
                        onClick={() => setHistoryExpanded(!historyExpanded)}
                      >
                        <IconHistory size={20} color={selectedEvent.color} />
                        <Title order={4} c='dark'>
                          Schedule History
                        </Title>
                        <Badge variant='filled' size='sm' color={selectedEvent.color}>
                          {selectedEvent.courseInstance.historyTransaksi.length} records
                        </Badge>
                        <ActionIcon
                          variant='subtle'
                          size='sm'
                          style={{
                            transform: historyExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}
                        >
                          <IconChevronDown size={16} />
                        </ActionIcon>
                      </Group>

                      {historyExpanded && (
                        <Stack gap='xs'>
                          {selectedEvent.courseInstance.historyTransaksi.map((history, index) => {
                            const startDate = new Date(history.startDate);
                            const endDate = new Date(history.endDate);
                            return (
                              <Paper
                                key={index}
                                p='sm'
                                radius='md'
                                style={{
                                  backgroundColor: '#f8f9ff',
                                  border: `1px solid ${selectedEvent.color}20`
                                }}
                              >
                                <Group justify='space-between'>
                                  <Box>
                                    <Text size='xs' c='dimmed' fw={500}>
                                      FROM
                                    </Text>
                                    <Text size='sm' fw={500} c='dark'>
                                      {startDate.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })}
                                    </Text>
                                    <Text size='xs' c='blue'>
                                      {startDate.toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false
                                      })}
                                    </Text>
                                  </Box>
                                  <Box ta='center'>
                                    <IconArrowRight size={16} color='gray' />
                                  </Box>
                                  <Box>
                                    <Text size='xs' c='dimmed' fw={500}>
                                      TO
                                    </Text>
                                    <Text size='sm' fw={500} c='dark'>
                                      {endDate.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })}
                                    </Text>
                                    <Text size='xs' c='blue'>
                                      {endDate.toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false
                                      })}
                                    </Text>
                                  </Box>
                                </Group>
                              </Paper>
                            );
                          })}
                        </Stack>
                      )}

                      {!historyExpanded &&
                        selectedEvent.courseInstance.historyTransaksi.length > 0 && (
                          <Text ta='center' size='sm' c='dimmed' style={{ fontStyle: 'italic' }}>
                            Click to view {selectedEvent.courseInstance.historyTransaksi.length}{' '}
                            schedule history records
                          </Text>
                        )}
                    </Box>
                  )}
                {/* Conflict Warning */}
                {selectedEvent.hasConflict && (
                  <Alert
                    icon={<IconExclamationMark size={16} />}
                    title='Schedule Conflict Detected'
                    color='orange'
                    radius='lg'
                    styles={{
                      root: {
                        backgroundColor: '#fff7ed',
                        border: '1px solid #fed7aa'
                      }
                    }}
                  >
                    This course schedule overlaps with other course instances. Please adjust the
                    dates to resolve conflicts.
                  </Alert>
                )}
              </Stack>
            </Box>

            {/* Footer */}
            <Box
              p='xl'
              style={{
                backgroundColor: '#f9fafb',
                borderTop: '1px solid #e5e7eb'
              }}
            >
              <Group justify='space-between'>
                <Button
                  variant='subtle'
                  color='red'
                  leftSection={<IconTrash size={16} />}
                  onClick={() => {
                    onRemoveInstance(selectedEvent.courseInstance.instanceId);
                    setEventModalOpened(false);
                    setHistoryExpanded(false);
                  }}
                  size='md'
                  radius='lg'
                  styles={{
                    root: {
                      '&:hover': {
                        backgroundColor: '#fee2e2'
                      }
                    }
                  }}
                >
                  Remove Course
                </Button>

                <Group>
                  <Button
                    variant='outline'
                    onClick={() => {
                      setEventModalOpened(false);
                      setHistoryExpanded(false);
                    }}
                    size='md'
                    radius='lg'
                    color='gray'
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      setEventModalOpened(false);
                      setHistoryExpanded(false);
                    }}
                    size='md'
                    radius='lg'
                    style={{
                      backgroundColor: selectedEvent.color,
                      '&:hover': {
                        backgroundColor: selectedEvent.color,
                        opacity: 0.9
                      }
                    }}
                    leftSection={<IconDeviceFloppy size={16} />}
                  >
                    Save Changes
                  </Button>
                </Group>
              </Group>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};

// Group course instances by course ID
const groupCourseInstances = (instances: CourseInstance[]) => {
  const grouped = new Map<string, CourseInstance[]>();

  instances.forEach((instance) => {
    const key = instance.msLCMSCourseID;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(instance);
  });

  return Array.from(grouped.entries()).map(([courseId, instances]) => ({
    courseId,
    courseName: instances[0].courseName,
    courseCode: instances[0].courseCode,
    deliveryType: instances[0].deliveryType,
    learningHours: instances[0].learningHours,
    instances: instances.sort((a, b) => {
      // Sort by start date, then by creation order
      if (!a.planningStart && !b.planningStart) return 0;
      if (!a.planningStart) return 1;
      if (!b.planningStart) return -1;
      return new Date(a.planningStart).getTime() - new Date(b.planningStart).getTime();
    }),
    hasConflicts: instances.some((instance) => instance.hasConflict),
    scheduledCount: instances.filter((instance) => instance.planningStart && instance.planningEnd)
      .length,
    totalInstances: instances.length
  }));
};

const getInstanceDisplayName = (instance: CourseInstance, index: number) => {
  if (instance.planningStart && instance.planningEnd) {
    const startDate = new Date(instance.planningStart);
    const endDate = new Date(instance.planningEnd);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return `Batch ${index + 1}: Invalid dates`;
    }

    const startStr = startDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    const endStr = endDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });

    const startTime = startDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const endTime = endDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // If same day, show: "Batch 1: Jan 15, 09:00 - 17:00"
    if (startDate.toDateString() === endDate.toDateString()) {
      return `Batch ${index + 1}: ${startStr}, ${startTime} - ${endTime}`;
    }

    // If different days, show: "Batch 1: Jan 15 09:00 - Jan 20 17:00"
    return `Batch ${index + 1}: ${startStr} ${startTime} - ${endStr} ${endTime}`;
  }
  return `Batch ${index + 1}: Unscheduled`;
};

interface Facilitator {
  name: string;
  email: string;
  type: string;
  binusianID: string;
  approvalLevel: string | null;
  linkURL: string;
}

interface FacilitatorsModalProps {
  opened: boolean;
  onClose: () => void;
  facilitators: Facilitator[];
  courseName: string;
}

const FacilitatorsModal: React.FC<FacilitatorsModalProps> = ({
  opened,
  onClose,
  facilitators,
  courseName
}) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap='xs'>
          <IconUsers size={20} />
          <Text fw={600}>Facilitators</Text>
        </Group>
      }
      size='md'
      centered
    >
      <Stack gap='md'>
        <Text size='sm' c='dimmed'>
          {courseName}
        </Text>

        {facilitators.length > 0 ? (
          <Stack gap='sm'>
            {facilitators.map((facilitator, index) => (
              <Paper key={index} p='md' withBorder radius='md'>
                <Group gap='sm' align='flex-start' wrap='nowrap'>
                  <Stack gap={0} style={{ flex: 1 }}>
                    <Group gap={8}>
                      <Text size='sm' fw={500}>
                        {facilitator.name}
                      </Text>
                      <Badge
                        size='sm'
                        variant='light'
                        color={facilitator.type === 'PIC Approval' ? 'violet' : 'teal'}
                      >
                        {facilitator.type}
                      </Badge>
                    </Group>
                    <Text size='xs' c='dimmed' mt={4}>
                      {facilitator.email}
                    </Text>
                    {facilitator.binusianID && (
                      <Text size='xs' c='dimmed' mt={2}>
                        ID: {facilitator.binusianID}
                      </Text>
                    )}
                  </Stack>
                </Group>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Center py={40}>
            <Stack align='center' gap='xs'>
              <IconUsers size={48} stroke={1.5} color='var(--mantine-color-gray-4)' />
              <Text c='dimmed' size='sm'>
                No facilitators assigned
              </Text>
            </Stack>
          </Center>
        )}
      </Stack>
    </Modal>
  );
};

const SchedulePlanEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State management
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [search, setSearch] = useState('');
  const [courseInstances, setCourseInstances] = useState<CourseInstance[]>([]);
  const [availableCourses, setAvailableCourses] = useState<AvailableCourse[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [conflicts, setConflicts] = useState<ConflictInfo[]>([]);
  const [deletedBatchIds, setDeletedBatchIds] = useState<string[]>([]);
  const [deletedSessionIds, setDeletedSessionIds] = useState<string[]>([]);

  const [expandedAccordions, setExpandedAccordions] = useState<string[]>([]);

  const [scheduleModalOpened, setScheduleModalOpened] = useState(false);
  const [facilitatorsModalOpened, setFacilitatorsModalOpened] = useState(false);
  const [selectedFacilitators, setSelectedFacilitators] = useState<Facilitator[]>([]);
  const [selectedCourseName, setSelectedCourseName] = useState('');

  const [assessmentStartDate, setAssessmentStartDate] = useState<Date | null>(null);
  const [assessmentEndDate, setAssessmentEndDate] = useState<Date | null>(null);

  // Validation state
  const [validationErrors, setValidationErrors] = useState<{
    batchErrors: { [instanceId: string]: { start?: string; end?: string } };
    sessionErrors: {
      [instanceId: string]: { [moduleIndex: number]: { start?: string; end?: string } };
    };
    assessmentErrors: { start?: string; end?: string };
  }>({ batchErrors: {}, sessionErrors: {}, assessmentErrors: {} });
  const totalBudgetTemp = useMemo(() => {
    return courseInstances.reduce((sum, c) => sum + ((c as any).budget || 0), 0);
  }, [courseInstances]);

  // Fetch schedule plan detail
  const {
    data: schedulePlanData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['yearlyCalendarSchedulePlanDetail', id],
    queryFn: () => yearlyCalendarSchedulePlanService.getSchedulePlanDetail(id!),
    enabled: !!id,
    refetchOnWindowFocus: false
  });

  // Fetch Mode of Delivery options
  const { data: dataModeOfDelivery, isPending: loadingModeOfDelivery } = useQuery({
    queryKey: ['getModeOfDelivery'],
    queryFn: () => courseService.getModeOfDelivery({ pageSize: 100, pageNumber: 1 }),
    staleTime: Infinity
  });

  const normalizeMode = (value?: string | null) => value?.trim().toLowerCase();

  const getSessionModeOfDeliveryOptions = (
    parentModeOfDelivery?: string,
    currentModuleType?: string
  ) => {
    const parent = normalizeMode(parentModeOfDelivery);
    const current = normalizeMode(currentModuleType);

    return (
      dataModeOfDelivery?.data
        .map((item: { id: number; option: string }) => {
          const isSameAsParent = normalizeMode(item.option) === parent;

          return {
            value: item.option,
            label:
              isSameAsParent && current === parent
                ? `${item.option} (not allowed for session)`
                : item.option,
            disabled: isSameAsParent
          };
        })
        .filter((item) => {
          return !item.disabled || normalizeMode(item.value) === current;
        }) || []
    );
  };

  const schedulePlan = schedulePlanData?.data;

  useEffect(() => {
    if (schedulePlan?.courses) {
      const instances: CourseInstance[] = schedulePlan.courses.map((course, index) => {
        return {
          ...(course as any),
          instanceId: `${course.msLCMSCourseID}-${course.trLCMSPlanningCoursesID || index}`,
          // Convert UTC dates from backend to local time for display and editing
          planningStart: course.planningStart
            ? convertUTCToLocal(ensureUTCFormat(course.planningStart))?.toISOString() || ''
            : '',
          planningEnd: course.planningEnd
            ? convertUTCToLocal(ensureUTCFormat(course.planningEnd))?.toISOString() || ''
            : '',
          // Convert history transaction dates from UTC to local time
          historyTransaksi:
            (course as any).historyTransaksi?.map((history: any) => ({
              ...history,
              startDate: history.startDate
                ? convertUTCToLocal(ensureUTCFormat(history.startDate))?.toISOString() || ''
                : '',
              endDate: history.endDate
                ? convertUTCToLocal(ensureUTCFormat(history.endDate))?.toISOString() || ''
                : ''
            })) || [],
          // Convert Session breakdown dates from UTC to local time
          listModule: course.listModule
            ? course.listModule.map((module: any) => ({
                TrModuleID: module.TrModuleID,
                moduleType: module.moduleType,
                moduleStart: module.moduleStart
                  ? convertUTCToLocal(ensureUTCFormat(module.moduleStart))?.toISOString() || ''
                  : '',
                moduleEnd: module.moduleEnd
                  ? convertUTCToLocal(ensureUTCFormat(module.moduleEnd))?.toISOString() || ''
                  : ''
              }))
            : undefined,
          budget: typeof course.budget === 'number' ? course.budget : undefined
        } as CourseInstance;
      });
      setCourseInstances(instances);

      const uniqueCourses = Array.from(
        new Map(
          schedulePlan.courses.map((course) => [
            course.msLCMSCourseID,
            {
              msLCMSCourseID: course.msLCMSCourseID,
              courseName: course.courseName,
              courseCode: course.courseCode,
              learningHours: course.learningHours,
              deliveryType: course.deliveryType,
              modeOfDelivery: course.modeOfDelivery,
              schoolName: course.schoolName
            }
          ])
        ).values()
      );
      setAvailableCourses(uniqueCourses);

      setAssessmentStartDate(
        schedulePlan.scheduleAssessmentStart
          ? convertUTCToLocal(ensureUTCFormat(schedulePlan.scheduleAssessmentStart))
          : null
      );
      setAssessmentEndDate(
        schedulePlan.scheduleAssessmentEnd
          ? convertUTCToLocal(ensureUTCFormat(schedulePlan.scheduleAssessmentEnd))
          : null
      );
    }
  }, [schedulePlan]);

  // Check for conflicts whenever course instances change
  // useEffect(() => {
  //   const newConflicts = detectConflicts(courseInstances);
  //   setConflicts(newConflicts);

  //   // Update course instances with conflict info (simplified to avoid infinite loops)
  //   if (newConflicts.length !== conflicts.length) {
  //     setCourseInstances((prev) =>
  //       prev.map((instance) => ({
  //         ...instance,
  //         hasConflict: newConflicts.some((conflict) => conflict.instanceId === instance.instanceId),
  //         conflictWith:
  //           newConflicts.find((conflict) => conflict.instanceId === instance.instanceId)
  //             ?.conflictsWith || []
  //       }))
  //     );
  //   }
  // }, [
  //   courseInstances.length,
  //   courseInstances.map((i) => `${i.planningStart}-${i.planningEnd}`).join(',')
  // ]);

  // Helper function to check if batch is new/never scheduled
  const isNewBatch = (instance: CourseInstance): boolean => {
    // A batch is new if it has no history
    return !instance.historyTransaksi || instance.historyTransaksi.length === 0;
  };

  // Helper function to check if batch dates have changed
  const hasBatchDatesChanged = (instance: CourseInstance): boolean => {
    // If there's no history, consider it as not changed (new instance)
    if (!instance.historyTransaksi || instance.historyTransaksi.length === 0) {
      return false;
    }

    // Get the most recent history entry (last item in the array)
    const latestHistory = instance.historyTransaksi[instance.historyTransaksi.length - 1];

    // Compare current dates with the latest history
    const currentStart = instance.planningStart ? new Date(instance.planningStart).getTime() : null;
    const currentEnd = instance.planningEnd ? new Date(instance.planningEnd).getTime() : null;
    const historyStart = latestHistory.startDate
      ? new Date(latestHistory.startDate).getTime()
      : null;
    const historyEnd = latestHistory.endDate ? new Date(latestHistory.endDate).getTime() : null;

    return currentStart !== historyStart || currentEnd !== historyEnd;
  };

  // Drag and drop handlers
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination } = result;

    if (
      source.droppableId === 'available-courses' &&
      destination.droppableId === 'selected-courses'
    ) {
      // Adding a new course instance
      const courseIndex = parseInt(result.draggableId.split('-')[1]);
      const course = availableCourses[courseIndex];

      if (course) {
        const newInstance: CourseInstance = {
          instanceId: generateInstanceId(course.msLCMSCourseID),
          msLCMSCourseID: course.msLCMSCourseID,
          courseName: course.courseName,
          courseCode: course.courseCode,
          learningHours: course.learningHours,
          deliveryType: course.deliveryType ?? '',
          planningStart: '',
          planningEnd: '',
          trLCMSPlanningCoursesID: '',
          reSchedule: true // New batches are always editable
        };

        setCourseInstances((prev) => [...prev, newInstance]);
        setHasChanges(true);

        // Auto-expand the accordion for the new course
        setExpandedAccordions((prev) =>
          prev.includes(course.msLCMSCourseID) ? prev : [...prev, course.msLCMSCourseID]
        );
      }
    } else if (
      source.droppableId === 'selected-courses' &&
      destination.droppableId === 'selected-courses'
    ) {
      // Reordering course instances
      const newInstances = Array.from(courseInstances);
      const [reorderedItem] = newInstances.splice(source.index, 1);
      newInstances.splice(destination.index, 0, reorderedItem);

      setCourseInstances(newInstances);
      setHasChanges(true);
    }
  };

  const handleDateChange = (instanceId: string, type: 'start' | 'end', date: Date | null) => {
    const updatedInstances = courseInstances.map((instance) => {
      if (instance.instanceId === instanceId) {
        const updatedInstance = { ...instance };

        if (type === 'start') {
          updatedInstance.planningStart = date ? date.toISOString() : '';
        } else {
          updatedInstance.planningEnd = date ? date.toISOString() : '';
        }

        // Validate date range using local times, only auto-adjust when changing dates (not times)
        if (updatedInstance.planningStart && updatedInstance.planningEnd && type === 'start') {
          const localStart = new Date(updatedInstance.planningStart);
          const localEnd = new Date(updatedInstance.planningEnd);

          if (localStart && localEnd) {
            const startMs = new Date(
              localStart.getFullYear(),
              localStart.getMonth(),
              localStart.getDate(),
              localStart.getHours(),
              localStart.getMinutes(),
              0,
              0
            ).getTime();
            const endMs = new Date(
              localEnd.getFullYear(),
              localEnd.getMonth(),
              localEnd.getDate(),
              localEnd.getHours(),
              localEnd.getMinutes(),
              0,
              0
            ).getTime();

            // Only auto-adjust if end is before start (when changing start date)
            if (endMs < startMs) {
              const adjustedEnd = new Date(startMs + 60 * 60 * 1000);
              updatedInstance.planningEnd = convertLocalToUTC(adjustedEnd);
              notifications.show({
                title: 'Adjusted End Time',
                message: 'End time was adjusted to be after the start time.',
                color: 'blue'
              });
            }
          }
        }

        // Check if batch dates have changed and set reSchedule flag
        if (hasBatchDatesChanged(updatedInstance)) {
          updatedInstance.reSchedule = true;
        }

        return updatedInstance;
      }
      return instance;
    });

    setCourseInstances(updatedInstances);
    setHasChanges(true);
  };

  const handleRemoveInstance = (instanceId: string) => {
    // Find the instance being deleted
    const instanceToDelete = courseInstances.find((instance) => instance.instanceId === instanceId);

    // If it has a trLCMSPlanningCoursesID, mark it for deletion
    if (instanceToDelete?.trLCMSPlanningCoursesID) {
      setDeletedBatchIds((prev) => [...prev, instanceToDelete.trLCMSPlanningCoursesID!]);
    }

    // Remove from local state immediately for UI feedback
    setCourseInstances((prev) => prev.filter((instance) => instance.instanceId !== instanceId));
    setHasChanges(true);

    notifications.show({
      title: 'Batch Marked for Deletion',
      message: 'This batch will be deleted when you click "Save Changes"',
      color: 'yellow'
    });
  };

  const handleUpdateInstance = (instanceId: string, updates: Partial<CourseInstance>) => {
    setCourseInstances((prev) =>
      prev.map((instance) =>
        instance.instanceId === instanceId ? { ...instance, ...updates } : instance
      )
    );
    setHasChanges(true);
  };

  const handleBudgetChange = (instanceId: string, value: number | string) => {
    const numeric = typeof value === 'string' ? Number(value) : value;
    if (!Number.isFinite(numeric)) return;
    setCourseInstances((prev) =>
      prev.map((inst) =>
        inst.instanceId === instanceId ? { ...inst, budget: numeric as number } : inst
      )
    );
    setHasChanges(true);
  };

  const handleCEAReadyToggle = (instanceId: string, checked: boolean) => {
    setCourseInstances((prev) =>
      prev.map((inst) => (inst.instanceId === instanceId ? { ...inst, isCEAReady: checked } : inst))
    );
    setHasChanges(true);
  };

  // const handleReScheduleToggle = (instanceId: string, checked: boolean) => {
  //   setCourseInstances((prev) =>
  //     prev.map((inst) => (inst.instanceId === instanceId ? { ...inst, reSchedule: checked } : inst))
  //   );
  //   setHasChanges(true);
  // };

  const handleAddModule = (instanceId: string) => {
    setCourseInstances((prev) =>
      prev.map((inst) => {
        if (inst.instanceId === instanceId) {
          // Only use parent dates if they are valid, otherwise leave empty
          const hasValidStart =
            inst.planningStart && new Date(inst.planningStart).toString() !== 'Invalid Date';
          const hasValidEnd =
            inst.planningEnd && new Date(inst.planningEnd).toString() !== 'Invalid Date';

          const newModule: ModuleBreakdown = {
            moduleType: '',
            moduleStart: hasValidStart ? inst.planningStart : '',
            moduleEnd: hasValidEnd ? inst.planningEnd : ''
          };
          return {
            ...inst,
            listModule: [...(inst.listModule || []), newModule]
          };
        }
        return inst;
      })
    );
    setHasChanges(true);
  };

  const handleRemoveModule = (instanceId: string, moduleIndex: number) => {
    setCourseInstances((prev) =>
      prev.map((inst) => {
        if (inst.instanceId === instanceId && inst.listModule) {
          const moduleToDelete = inst.listModule[moduleIndex];
          // Store TrModuleID for deletion if it exists
          if (moduleToDelete?.TrModuleID) {
            setDeletedSessionIds((prevIds) => [...prevIds, moduleToDelete.TrModuleID!]);
          }
          return {
            ...inst,
            listModule: inst.listModule.filter((_, index) => index !== moduleIndex)
          };
        }
        return inst;
      })
    );
    setHasChanges(true);
  };

  const handleModuleChange = (
    instanceId: string,
    moduleIndex: number,
    field: keyof ModuleBreakdown,
    value: string
  ) => {
    setCourseInstances((prev) =>
      prev.map((inst) => {
        if (inst.instanceId === instanceId && inst.listModule) {
          const updatedModules = inst.listModule.map((module, index) => {
            if (index === moduleIndex) {
              return { ...module, [field]: value };
            }
            return module;
          });
          return { ...inst, listModule: updatedModules };
        }
        return inst;
      })
    );
    setHasChanges(true);
  };

  const handleModuleDateChange = (
    instanceId: string,
    moduleIndex: number,
    field: 'moduleStart' | 'moduleEnd',
    date: Date | null
  ) => {
    if (!date) return;
    const dateString = convertLocalToUTC(date);
    handleModuleChange(instanceId, moduleIndex, field, dateString);

    // Set reSchedule flag when session dates are changed
    setCourseInstances((prev) =>
      prev.map((instance) => {
        if (instance.instanceId === instanceId) {
          return {
            ...instance,
            reSchedule: true
          };
        }
        return instance;
      })
    );
  };

  const handleDuplicateInstance = (instanceId: string) => {
    const originalInstance = courseInstances.find((instance) => instance.instanceId === instanceId);
    if (originalInstance) {
      const newInstance: CourseInstance = {
        ...originalInstance,
        instanceId: generateInstanceId(originalInstance.msLCMSCourseID),
        trLCMSPlanningCoursesID: '', // Empty string will be converted to null in payload
        planningStart: '',
        planningEnd: '',
        listModule: undefined, // Clear module breakdown for new instance
        reSchedule: true, // New batches are always editable
        historyTransaksi: undefined // Clear history for new instance
      };

      setCourseInstances((prev) => [...prev, newInstance]);
      setHasChanges(true);

      // Auto-expand the accordion
      setExpandedAccordions((prev) =>
        prev.includes(originalInstance.msLCMSCourseID)
          ? prev
          : [...prev, originalInstance.msLCMSCourseID]
      );
    }
  };

  // Validation function
  const validateDates = (): boolean => {
    const errors = {
      batchErrors: {} as { [instanceId: string]: { start?: string; end?: string } },
      sessionErrors: {} as {
        [instanceId: string]: { [moduleIndex: number]: { start?: string; end?: string } };
      },
      assessmentErrors: {} as { start?: string; end?: string }
    };

    let hasErrors = false;

    courseInstances.forEach((instance) => {
      const batchError: { start?: string; end?: string } = {};

      const hasStartDate = !!instance.planningStart;
      const hasEndDate = !!instance.planningEnd;

      if (hasStartDate && !hasEndDate) {
        batchError.end = 'End date is required when start date is set';
        hasErrors = true;
      }

      if (!hasStartDate && hasEndDate) {
        batchError.start = 'Start date is required when end date is set';
        hasErrors = true;
      }

      // Validate date range if both dates are provided
      if (hasStartDate && hasEndDate) {
        const startDate = new Date(instance.planningStart);
        const endDate = new Date(instance.planningEnd);

        if (startDate >= endDate) {
          batchError.end = 'End date must be after start date';
          hasErrors = true;
        }
      }

      if (Object.keys(batchError).length > 0) {
        errors.batchErrors[instance.instanceId] = batchError;
      }

      // Validate session dates for this instance
      if (instance.listModule && instance.listModule.length > 0) {
        const sessionInstanceErrors: { [moduleIndex: number]: { start?: string; end?: string } } =
          {};

        instance.listModule.forEach((module, moduleIndex) => {
          const sessionError: { start?: string; end?: string } = {};

          if (!module.moduleStart) {
            sessionError.start = 'Session start date is required';
            hasErrors = true;
          }

          if (!module.moduleEnd) {
            sessionError.end = 'Session end date is required';
            hasErrors = true;
          }

          if (module.moduleStart && module.moduleEnd) {
            const sessionStartDate = new Date(module.moduleStart);
            const sessionEndDate = new Date(module.moduleEnd);

            if (sessionStartDate >= sessionEndDate) {
              sessionError.end = 'Session end date must be after start date';
              hasErrors = true;
            }

            // Validate session dates are within batch dates
            if (instance.planningStart && instance.planningEnd) {
              const batchStartDate = new Date(instance.planningStart);
              const batchEndDate = new Date(instance.planningEnd);

              if (sessionStartDate < batchStartDate) {
                sessionError.start = 'Session start date must be within batch dates';
                hasErrors = true;
              }

              if (sessionEndDate > batchEndDate) {
                sessionError.end = 'Session end date must be within batch dates';
                hasErrors = true;
              }
            }
          }

          if (Object.keys(sessionError).length > 0) {
            sessionInstanceErrors[moduleIndex] = sessionError;
          }
        });

        if (Object.keys(sessionInstanceErrors).length > 0) {
          errors.sessionErrors[instance.instanceId] = sessionInstanceErrors;
        }
      }
    });

    // Validate assessment dates - require both if one is provided
    const hasAssessmentStart = !!assessmentStartDate;
    const hasAssessmentEnd = !!assessmentEndDate;

    if (hasAssessmentStart && hasAssessmentEnd) {
      if (assessmentStartDate >= assessmentEndDate) {
        errors.assessmentErrors.end = 'Assessment end date must be after start date';
        hasErrors = true;
      }
    }

    setValidationErrors(errors);
    return !hasErrors;
  };

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateSchedulePlanDto) =>
      yearlyCalendarSchedulePlanService.updateSchedulePlan(data),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Schedule plan updated successfully',
        color: 'green'
      });
      setHasChanges(false);
      setValidationErrors({ batchErrors: {}, sessionErrors: {}, assessmentErrors: {} });
      queryClient.invalidateQueries({ queryKey: ['yearlyCalendarSchedulePlanDetail', id] });
      queryClient.invalidateQueries({ queryKey: ['yearlyCalendarSchedulePlans'] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to update schedule plan',
        color: 'red'
      });
    }
  });

  const handleSave = async () => {
    if (!schedulePlan) return;

    if (!validateDates()) {
      notifications.show({
        title: 'Validation Error',
        message:
          'Please fix the following: (1) Complete date pairs for courses (both start and end), (2) Ensure all end dates are after start dates. Note: Batches without dates will not be saved.',
        color: 'red',
        autoClose: 7000
      });
      return;
    }

    if (deletedSessionIds.length > 0) {
      try {
        notifications.show({
          id: 'deleting-sessions',
          title: 'Deleting Sessions',
          message: `Deleting ${deletedSessionIds.length} session(s)...`,
          color: 'blue',
          loading: true,
          autoClose: false
        });

        for (const sessionId of deletedSessionIds) {
          await yearlyCalendarSchedulePlanService.deleteSession(sessionId);
        }

        setDeletedSessionIds([]);

        notifications.update({
          id: 'deleting-sessions',
          title: 'Sessions Deleted',
          message: `Successfully deleted ${deletedSessionIds.length} session(s)`,
          color: 'green',
          loading: false,
          autoClose: 3000
        });
      } catch (error: any) {
        notifications.update({
          id: 'deleting-sessions',
          title: 'Error',
          message: error?.message || 'Failed to delete some sessions',
          color: 'red',
          loading: false,
          autoClose: 5000
        });
        return;
      }
    }

    // 2. Then delete batches
    if (deletedBatchIds.length > 0) {
      try {
        // Show notification for deletion process
        notifications.show({
          id: 'deleting-batches',
          title: 'Deleting Batches',
          message: `Deleting ${deletedBatchIds.length} batch(es)...`,
          color: 'blue',
          loading: true,
          autoClose: false
        });

        // Delete batches sequentially
        for (const batchId of deletedBatchIds) {
          await yearlyCalendarSchedulePlanService.deleteBatch(batchId);
        }

        // Clear the deleted IDs after successful deletion
        setDeletedBatchIds([]);

        // Update notification
        notifications.update({
          id: 'deleting-batches',
          title: 'Batches Deleted',
          message: `Successfully deleted ${deletedBatchIds.length} batch(es)`,
          color: 'green',
          loading: false,
          autoClose: 3000
        });
      } catch (error: any) {
        notifications.update({
          id: 'deleting-batches',
          title: 'Error',
          message: error?.message || 'Failed to delete some batches',
          color: 'red',
          loading: false,
          autoClose: 5000
        });
        return; // Don't proceed with update if deletion failed
      }
    }

    // Then proceed with updates if there are changes
    const payload = convertInstancesToUpdateDto(
      schedulePlan.msLCMSPlanningID,
      courseInstances,
      assessmentStartDate,
      assessmentEndDate
    );

    updateMutation.mutate(payload);
  };

  const handleBack = () => {
    navigate(pathnames.schedulePlan);
  };

  // Filter courses based on search
  const filteredInstances = courseInstances
    .filter(
      (instance) =>
        instance.courseName.toLowerCase().includes(search.toLowerCase()) ||
        instance.courseCode.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (!a.planningStart && !b.planningStart) {
        return a.courseName.localeCompare(b.courseName);
      }
      if (!a.planningStart) return 1;
      if (!b.planningStart) return -1;

      const dateComparison =
        new Date(a.planningStart).getTime() - new Date(b.planningStart).getTime();

      if (dateComparison === 0) {
        return a.courseName.localeCompare(b.courseName);
      }

      return dateComparison;
    });

  const filteredAvailableCourses = availableCourses.filter(
    (course) =>
      course.courseName.toLowerCase().includes(search.toLowerCase()) ||
      course.courseCode.toLowerCase().includes(search.toLowerCase())
  );

  if (!id) {
    navigate(pathnames.schedulePlan);
    return null;
  }

  if (isLoading) {
    return <SchedulePlanEditSkeleton />;
  }

  if (error) {
    return (
      <Stack gap='md'>
        <BreadcrumbsComponent
          items={[
            { title: 'Schedule Plan', href: pathnames.schedulePlan },
            { title: 'Edit Schedules Plan', href: '' }
          ]}
        />
        <Alert icon={<IconAlertCircle size={16} />} title='Error' color='red'>
          Failed to load schedule plan details. Please try again later.
        </Alert>
      </Stack>
    );
  }

  if (!schedulePlan) {
    return null;
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Stack gap='md'>
        <Group justify='space-between' align='flex-start'>
          <BreadcrumbsComponent
            items={[
              { title: 'Schedule Plan', href: pathnames.schedulePlan },
              { title: 'Edit Schedules Plan', href: '' }
            ]}
          />
          <Group>
            {/* <Menu shadow='md' width={200}>
              <Menu.Target>
                <ActionIcon variant='outline' size='lg'>
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Schedule Management</Menu.Label>
                <Menu.Item
                  leftSection={<IconFileSpreadsheet size={14} />}
                  onClick={() => setScheduleModalOpened(true)}
                >
                  Import/Export Excel
                </Menu.Item>
              </Menu.Dropdown>
            </Menu> */}

            <SegmentedControl
              value={viewMode}
              onChange={(value) => setViewMode(value as ViewMode)}
              data={[
                { label: 'Table View', value: 'table' },
                { label: 'Calendar View', value: 'calendar' }
              ]}
            />
          </Group>
        </Group>

        {/* Conflict Alert */}
        {conflicts.length > 0 && (
          <Alert
            icon={<IconExclamationMark size={16} />}
            title='Schedule Conflicts Detected'
            color='orange'
          >
            There are {conflicts.length} course schedule conflicts. Please review and adjust the
            dates.
          </Alert>
        )}

        <Grid gutter='lg'>
          <Grid.Col span={viewMode === 'calendar' ? 3 : 3}>
            <Paper p='md' radius='md' withBorder>
              <Stack>
                <Group justify='space-between'>
                  <Box>
                    <Title order={5}>Available Courses</Title>
                    <Text size='sm' c='dimmed'>
                      Drag to schedule
                    </Text>
                  </Box>
                  <Badge size='sm' variant='light'>
                    {availableCourses.length}
                  </Badge>
                </Group>

                <TextInput
                  placeholder='Search courses...'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  leftSection={<IconSearch size={16} />}
                  size='sm'
                />

                <Droppable droppableId='available-courses' isDropDisabled>
                  {(provided) => (
                    <ScrollArea h={400} {...provided.droppableProps} ref={provided.innerRef}>
                      <Stack gap='xs'>
                        {filteredAvailableCourses.map((course, index) => (
                          <Draggable
                            key={course.msLCMSCourseID}
                            draggableId={`course-${index}`}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Paper
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                p='sm'
                                withBorder
                                radius='sm'
                                style={{
                                  ...provided.draggableProps.style,
                                  cursor: 'grab',
                                  backgroundColor: snapshot.isDragging ? '#f1f3f4' : 'white',
                                  borderColor: snapshot.isDragging ? '#1976d2' : '#e0e0e0'
                                }}
                              >
                                <Stack gap='xs'>
                                  <Group justify='space-between' align='flex-start'>
                                    <Box style={{ flex: 1 }}>
                                      <Text fw={600} size='sm' lineClamp={2}>
                                        {course.courseName}
                                      </Text>
                                      <Text size='xs' c='dimmed'>
                                        {course.courseCode}
                                      </Text>
                                    </Box>
                                    <IconGripVertical size={16} color='gray' />
                                  </Group>

                                  {/* Display learning hours and delivery type */}
                                  <Group gap='xs'>
                                    <Badge size='xs' variant='light' color='blue'>
                                      {course.learningHours}h
                                    </Badge>
                                    {course.deliveryType && (
                                      <Badge size='xs' variant='light' color='green'>
                                        {course.deliveryType}
                                      </Badge>
                                    )}
                                  </Group>

                                  {(course.schoolName ||
                                    course.modeOfDelivery ||
                                    (course.facilitators && course.facilitators.length > 0)) && (
                                    <Stack gap={4}>
                                      {course.schoolName && (
                                        <Group gap='xs'>
                                          <Text size='xs' c='dimmed' fw={500}>
                                            School:
                                          </Text>
                                          <Text size='xs' c='dark'>
                                            {course.schoolName}
                                          </Text>
                                        </Group>
                                      )}
                                      {course.modeOfDelivery && (
                                        <Group gap='xs'>
                                          <Text size='xs' c='dimmed' fw={500}>
                                            Mode:
                                          </Text>
                                          <Badge size='xs' variant='outline' color='purple'>
                                            {course.modeOfDelivery}
                                          </Badge>
                                        </Group>
                                      )}
                                      {course.facilitators && course.facilitators.length > 0 && (
                                        <Button
                                          size='xs'
                                          variant='light'
                                          color='teal'
                                          leftSection={<IconUsers size={14} />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedFacilitators(course.facilitators || []);
                                            setSelectedCourseName(course.courseName);
                                            setFacilitatorsModalOpened(true);
                                          }}
                                          fullWidth
                                        >
                                          {course.facilitators.length} Facilitator
                                          {course.facilitators.length !== 1 ? 's' : ''}
                                        </Button>
                                      )}
                                    </Stack>
                                  )}
                                </Stack>
                              </Paper>
                            )}
                          </Draggable>
                        ))}
                      </Stack>
                      {provided.placeholder}
                    </ScrollArea>
                  )}
                </Droppable>

                <Text size='xs' c='dimmed' ta='center' style={{ fontStyle: 'italic' }}>
                  💡 Drag courses to the schedule area to add them
                </Text>
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={viewMode === 'calendar' ? 9 : 6}>
            {viewMode === 'table' ? (
              <Paper p='md' radius='md' pos='relative'>
                <LoadingOverlay visible={updateMutation.isPending} />

                <Group justify='space-between' mb='md'>
                  <Title order={4}>Course Schedule</Title>
                  <Group>
                    {hasChanges && (
                      <Button
                        leftSection={<IconDeviceFloppy size={16} />}
                        onClick={handleSave}
                        loading={updateMutation.isPending}
                      >
                        Save Changes
                      </Button>
                    )}
                  </Group>
                </Group>

                <Droppable droppableId='selected-courses'>
                  {(provided) => (
                    <Box {...provided.droppableProps} ref={provided.innerRef}>
                      {(() => {
                        const groupedCourses = groupCourseInstances(filteredInstances);

                        if (groupedCourses.length === 0) {
                          return (
                            <Center py={60}>
                              <Stack align='center' gap='md'>
                                <Box
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '50%',
                                    padding: '20px',
                                    border: '2px dashed #dee2e6'
                                  }}
                                >
                                  <IconPlus size={32} color='#6c757d' />
                                </Box>
                                <Box ta='center'>
                                  <Text fw={600} size='lg' mb='xs' c='dark'>
                                    No courses scheduled
                                  </Text>
                                  <Text c='dimmed' size='sm'>
                                    Drag courses from the left panel to start scheduling
                                  </Text>
                                </Box>
                              </Stack>
                            </Center>
                          );
                        }

                        return (
                          <Stack gap='md'>
                            {groupedCourses.map((courseGroup) => (
                              <Paper
                                key={courseGroup.courseId}
                                withBorder
                                radius='md'
                                style={{
                                  borderColor: courseGroup.hasConflicts ? '#fa5252' : '#dee2e6',
                                  borderWidth: courseGroup.hasConflicts ? 2 : 1,
                                  overflow: 'hidden'
                                }}
                              >
                                <Box
                                  p='md'
                                  style={{
                                    backgroundColor: courseGroup.hasConflicts
                                      ? '#fff5f5'
                                      : '#f8f9fa',
                                    borderBottom: '1px solid #dee2e6'
                                  }}
                                >
                                  <Group justify='space-between' align='center'>
                                    <Box flex={1}>
                                      <Group gap='sm' mb='xs'>
                                        <Text fw={600} size='md' c='dark'>
                                          {courseGroup.courseName}
                                        </Text>
                                        {courseGroup.hasConflicts && (
                                          <Tooltip label='Schedule conflicts detected'>
                                            <IconExclamationMark size={16} color='#fa5252' />
                                          </Tooltip>
                                        )}
                                      </Group>

                                      <Group gap='xs' wrap='wrap'>
                                        <Badge variant='outline' size='sm'>
                                          {courseGroup.courseCode}
                                        </Badge>
                                        <Badge variant='light' color='blue' size='sm'>
                                          {courseGroup.learningHours}h
                                        </Badge>
                                        <Badge variant='light' color='green' size='sm'>
                                          {courseGroup.deliveryType}
                                        </Badge>
                                        {courseGroup.instances[0]?.schoolName && (
                                          <Group gap={4}>
                                            <Text size='xs' fw={500}>
                                              {courseGroup.instances[0].schoolName}
                                            </Text>
                                          </Group>
                                        )}
                                        {courseGroup.instances[0]?.modeOfDelivery && (
                                          <Badge size='xs' variant='outline' color='purple'>
                                            {courseGroup.instances[0].modeOfDelivery}
                                          </Badge>
                                        )}
                                        {courseGroup.instances[0]?.facilitators &&
                                          courseGroup.instances[0].facilitators.length > 0 && (
                                            <Tooltip label='View facilitators'>
                                              <ActionIcon
                                                size='sm'
                                                variant='light'
                                                color='teal'
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setSelectedFacilitators(
                                                    courseGroup.instances[0].facilitators || []
                                                  );
                                                  setSelectedCourseName(courseGroup.courseName);
                                                  setFacilitatorsModalOpened(true);
                                                }}
                                              >
                                                <IconUsers size={14} />
                                              </ActionIcon>
                                            </Tooltip>
                                          )}
                                        <Text size='sm' c='dimmed'>
                                          • {courseGroup.totalInstances} batch
                                          {courseGroup.totalInstances !== 1 ? 'es' : ''}
                                        </Text>
                                      </Group>
                                    </Box>

                                    <Group gap='xs'>
                                      <Badge
                                        variant={
                                          courseGroup.scheduledCount === courseGroup.totalInstances
                                            ? 'filled'
                                            : 'outline'
                                        }
                                        color={
                                          courseGroup.scheduledCount === courseGroup.totalInstances
                                            ? 'green'
                                            : 'orange'
                                        }
                                        size='sm'
                                      >
                                        {courseGroup.scheduledCount}/{courseGroup.totalInstances}{' '}
                                        scheduled
                                      </Badge>

                                      <Tooltip label='Add another batch'>
                                        <ActionIcon
                                          variant='subtle'
                                          color='blue'
                                          onClick={() =>
                                            handleDuplicateInstance(
                                              courseGroup.instances[0].instanceId
                                            )
                                          }
                                        >
                                          <IconPlus size={16} />
                                        </ActionIcon>
                                      </Tooltip>

                                      <ActionIcon
                                        variant='subtle'
                                        onClick={() => {
                                          const isExpanded = expandedAccordions.includes(
                                            courseGroup.courseId
                                          );
                                          if (isExpanded) {
                                            setExpandedAccordions((prev) =>
                                              prev.filter((id) => id !== courseGroup.courseId)
                                            );
                                          } else {
                                            setExpandedAccordions((prev) => [
                                              ...prev,
                                              courseGroup.courseId
                                            ]);
                                          }
                                        }}
                                        style={{
                                          transform: expandedAccordions.includes(
                                            courseGroup.courseId
                                          )
                                            ? 'rotate(180deg)'
                                            : 'rotate(0deg)',
                                          transition: 'transform 0.2s ease'
                                        }}
                                      >
                                        <IconArrowLeft
                                          style={{ transform: 'rotate(-90deg)' }}
                                          size={16}
                                        />
                                      </ActionIcon>
                                    </Group>
                                  </Group>
                                </Box>

                                {/* Course Instances */}
                                <Collapse in={expandedAccordions.includes(courseGroup.courseId)}>
                                  <Box p='md'>
                                    <Stack gap='sm'>
                                      {courseGroup.instances.map((instance, instanceIndex) => {
                                        const globalIndex = filteredInstances.findIndex(
                                          (item) => item.instanceId === instance.instanceId
                                        );

                                        return (
                                          <Draggable
                                            key={instance.instanceId}
                                            draggableId={instance.instanceId}
                                            index={globalIndex}
                                          >
                                            {(provided, snapshot) => (
                                              <Paper
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                p='md'
                                                withBorder
                                                radius='sm'
                                                style={{
                                                  ...provided.draggableProps.style,
                                                  backgroundColor: snapshot.isDragging
                                                    ? '#f1f3f4'
                                                    : instance.hasConflict
                                                      ? '#fff5f5'
                                                      : 'white',
                                                  borderColor: instance.hasConflict
                                                    ? '#fa5252'
                                                    : snapshot.isDragging
                                                      ? '#228be6'
                                                      : '#dee2e6',
                                                  borderStyle: instance.hasConflict
                                                    ? 'dashed'
                                                    : 'solid',
                                                  boxShadow: snapshot.isDragging
                                                    ? '0 4px 12px rgba(0, 0, 0, 0.15)'
                                                    : undefined
                                                }}
                                              >
                                                <Grid align='center'>
                                                  <Grid.Col span={1}>
                                                    <Group justify='center'>
                                                      <div {...provided.dragHandleProps}>
                                                        <IconGripVertical
                                                          size={16}
                                                          color='gray'
                                                          style={{ cursor: 'grab' }}
                                                        />
                                                      </div>
                                                    </Group>
                                                  </Grid.Col>

                                                  <Grid.Col span={11}>
                                                    <Box>
                                                      <Group gap='xs' align='center' mb={4}>
                                                        <Text fw={500} size='sm' c='dark'>
                                                          {getInstanceDisplayName(
                                                            instance,
                                                            instanceIndex
                                                          )}
                                                        </Text>
                                                        {instance.hasConflict && (
                                                          <Badge
                                                            color='red'
                                                            variant='light'
                                                            size='xs'
                                                          >
                                                            Conflict
                                                          </Badge>
                                                        )}
                                                        {instance.statusCEA === 0 && (
                                                          <Badge
                                                            color='gray'
                                                            variant='filled'
                                                            size='xs'
                                                            leftSection={<IconLock size={10} />}
                                                          >
                                                            Locked
                                                          </Badge>
                                                        )}
                                                      </Group>
                                                      <Text size='xs' c='dimmed'>
                                                        Batch {instanceIndex + 1} of{' '}
                                                        {courseGroup.totalInstances}
                                                      </Text>
                                                    </Box>
                                                  </Grid.Col>

                                                  <Grid.Col span={8} pos='relative'>
                                                    <LoadingOverlay
                                                      visible={instance.statusCEA === 0}
                                                      loaderProps={{
                                                        children: (
                                                          <Stack align='center' gap='xs'>
                                                            <IconLock size={40} color='#868e96' />
                                                            <Text size='sm' fw={500} c='dimmed'>
                                                              Batch Locked
                                                            </Text>
                                                            <Text size='xs' c='dimmed' ta='center'>
                                                              This batch is locked (CEA Status: 0)
                                                              <br />
                                                              and cannot be edited
                                                            </Text>
                                                          </Stack>
                                                        )
                                                      }}
                                                      overlayProps={{
                                                        radius: 'sm',
                                                        blur: 2,
                                                        color: '#fff',
                                                        opacity: 0.9
                                                      }}
                                                    />
                                                    <Stack gap='xs'>
                                                      {/* Start Date and Time Row */}
                                                      <Group gap='xs' align='flex-end'>
                                                        <Box style={{ flex: 2 }}>
                                                          <DateInput
                                                            label='Start Date'
                                                            placeholder='DD/MM/YYYY'
                                                            value={
                                                              instance.planningStart
                                                                ? new Date(instance.planningStart)
                                                                : null
                                                            }
                                                            disabled={
                                                              !isNewBatch(instance) &&
                                                              !instance.reSchedule
                                                            }
                                                            onChange={(date) => {
                                                              if (date) {
                                                                // Preserve existing time or default to 00:00
                                                                const currentDate =
                                                                  instance.planningStart
                                                                    ? new Date(
                                                                        instance.planningStart
                                                                      )
                                                                    : new Date(date);
                                                                currentDate.setFullYear(
                                                                  date.getFullYear()
                                                                );
                                                                currentDate.setMonth(
                                                                  date.getMonth()
                                                                );
                                                                currentDate.setDate(date.getDate());
                                                                handleDateChange(
                                                                  instance.instanceId,
                                                                  'start',
                                                                  currentDate
                                                                );
                                                              }
                                                              // Clear validation error when user makes a change
                                                              if (
                                                                validationErrors.batchErrors[
                                                                  instance.instanceId
                                                                ]?.start
                                                              ) {
                                                                setValidationErrors((prev) => ({
                                                                  ...prev,
                                                                  batchErrors: {
                                                                    ...prev.batchErrors,
                                                                    [instance.instanceId]: {
                                                                      ...prev.batchErrors[
                                                                        instance.instanceId
                                                                      ],
                                                                      start: undefined
                                                                    }
                                                                  }
                                                                }));
                                                              }
                                                            }}
                                                            valueFormat='DD/MM/YYYY'
                                                            size='sm'
                                                            leftSection={<IconCalendar size={14} />}
                                                            popoverProps={{
                                                              position: 'bottom',
                                                              withinPortal: true
                                                            }}
                                                            error={
                                                              validationErrors.batchErrors[
                                                                instance.instanceId
                                                              ]?.start
                                                            }
                                                            styles={{
                                                              label: {
                                                                fontWeight: 500,
                                                                fontSize: '12px',
                                                                marginBottom: 4
                                                              },
                                                              input: {
                                                                borderColor: validationErrors
                                                                  .batchErrors[instance.instanceId]
                                                                  ?.start
                                                                  ? '#fa5252'
                                                                  : instance.hasConflict
                                                                    ? '#fa5252'
                                                                    : undefined,
                                                                fontSize: '13px'
                                                              }
                                                            }}
                                                          />
                                                        </Box>
                                                        <Box style={{ flex: 1 }}>
                                                          <TimeInput
                                                            label='Time'
                                                            placeholder='HH:mm'
                                                            value={
                                                              instance.planningStart
                                                                ? dayjs(
                                                                    instance.planningStart
                                                                  ).format('HH:mm')
                                                                : ''
                                                            }
                                                            disabled={
                                                              !isNewBatch(instance) &&
                                                              !instance.reSchedule
                                                            }
                                                            onChange={(event) => {
                                                              const timeValue =
                                                                event.currentTarget.value;
                                                              if (
                                                                timeValue &&
                                                                instance.planningStart
                                                              ) {
                                                                const [hours, minutes] = timeValue
                                                                  .split(':')
                                                                  .map(Number);
                                                                const currentDate = new Date(
                                                                  instance.planningStart
                                                                );
                                                                currentDate.setHours(
                                                                  hours,
                                                                  minutes,
                                                                  0,
                                                                  0
                                                                );
                                                                handleDateChange(
                                                                  instance.instanceId,
                                                                  'start',
                                                                  currentDate
                                                                );
                                                              }
                                                            }}
                                                            size='sm'
                                                            styles={{
                                                              label: {
                                                                fontWeight: 500,
                                                                fontSize: '12px',
                                                                marginBottom: 4
                                                              },
                                                              input: {
                                                                fontSize: '13px'
                                                              }
                                                            }}
                                                          />
                                                        </Box>
                                                      </Group>

                                                      {/* End Date and Time Row */}
                                                      <Group gap='xs' align='flex-end'>
                                                        <Box style={{ flex: 2 }}>
                                                          <DateInput
                                                            label='End Date'
                                                            placeholder='DD/MM/YYYY'
                                                            value={
                                                              instance.planningEnd
                                                                ? new Date(instance.planningEnd)
                                                                : null
                                                            }
                                                            disabled={
                                                              !isNewBatch(instance) &&
                                                              !instance.reSchedule
                                                            }
                                                            onChange={(date) => {
                                                              if (date) {
                                                                // Preserve existing time or default to 23:59
                                                                const currentDate =
                                                                  instance.planningEnd
                                                                    ? new Date(instance.planningEnd)
                                                                    : new Date(date);
                                                                currentDate.setFullYear(
                                                                  date.getFullYear()
                                                                );
                                                                currentDate.setMonth(
                                                                  date.getMonth()
                                                                );
                                                                currentDate.setDate(date.getDate());
                                                                handleDateChange(
                                                                  instance.instanceId,
                                                                  'end',
                                                                  currentDate
                                                                );
                                                              }
                                                              // Clear validation error when user makes a change
                                                              if (
                                                                validationErrors.batchErrors[
                                                                  instance.instanceId
                                                                ]?.end
                                                              ) {
                                                                setValidationErrors((prev) => ({
                                                                  ...prev,
                                                                  batchErrors: {
                                                                    ...prev.batchErrors,
                                                                    [instance.instanceId]: {
                                                                      ...prev.batchErrors[
                                                                        instance.instanceId
                                                                      ],
                                                                      end: undefined
                                                                    }
                                                                  }
                                                                }));
                                                              }
                                                            }}
                                                            valueFormat='DD/MM/YYYY'
                                                            size='sm'
                                                            leftSection={<IconCalendar size={14} />}
                                                            popoverProps={{
                                                              position: 'bottom',
                                                              withinPortal: true
                                                            }}
                                                            error={
                                                              validationErrors.batchErrors[
                                                                instance.instanceId
                                                              ]?.end
                                                            }
                                                            styles={{
                                                              label: {
                                                                fontWeight: 500,
                                                                fontSize: '12px',
                                                                marginBottom: 4
                                                              },
                                                              input: {
                                                                borderColor: validationErrors
                                                                  .batchErrors[instance.instanceId]
                                                                  ?.end
                                                                  ? '#fa5252'
                                                                  : instance.hasConflict
                                                                    ? '#fa5252'
                                                                    : undefined,
                                                                fontSize: '13px'
                                                              }
                                                            }}
                                                          />
                                                        </Box>
                                                        <Box style={{ flex: 1 }}>
                                                          <TimeInput
                                                            label='Time'
                                                            placeholder='HH:mm'
                                                            value={
                                                              instance.planningEnd
                                                                ? dayjs(
                                                                    instance.planningEnd
                                                                  ).format('HH:mm')
                                                                : ''
                                                            }
                                                            disabled={
                                                              !isNewBatch(instance) &&
                                                              !instance.reSchedule
                                                            }
                                                            onChange={(event) => {
                                                              const timeValue =
                                                                event.currentTarget.value;
                                                              if (
                                                                timeValue &&
                                                                instance.planningEnd
                                                              ) {
                                                                const [hours, minutes] = timeValue
                                                                  .split(':')
                                                                  .map(Number);
                                                                const currentDate = new Date(
                                                                  instance.planningEnd
                                                                );
                                                                currentDate.setHours(
                                                                  hours,
                                                                  minutes,
                                                                  0,
                                                                  0
                                                                );
                                                                handleDateChange(
                                                                  instance.instanceId,
                                                                  'end',
                                                                  currentDate
                                                                );
                                                              }
                                                            }}
                                                            size='sm'
                                                            styles={{
                                                              label: {
                                                                fontWeight: 500,
                                                                fontSize: '12px',
                                                                marginBottom: 4
                                                              },
                                                              input: {
                                                                fontSize: '13px'
                                                              }
                                                            }}
                                                          />
                                                        </Box>
                                                      </Group>
                                                    </Stack>
                                                  </Grid.Col>
                                                  <Grid.Col span={4}>
                                                    <NumberInput
                                                      label='Budget Planning (IDR)'
                                                      placeholder='0'
                                                      value={instance.budget ?? undefined}
                                                      onChange={(val) =>
                                                        handleBudgetChange(instance.instanceId, val)
                                                      }
                                                      thousandSeparator
                                                      hideControls
                                                      min={0}
                                                      size='sm'
                                                    />
                                                  </Grid.Col>
                                                </Grid>

                                                {/* CEA Ready Switch */}
                                                <Box mt='md'>
                                                  <Group gap='sm' mb='sm'>
                                                    <IconCalendarEvent size={16} color='#228be6' />
                                                    <Text fw={600} size='sm' c='dark'>
                                                      CEA Ready Status
                                                    </Text>
                                                  </Group>

                                                  <Paper
                                                    p='sm'
                                                    radius='md'
                                                    style={{
                                                      backgroundColor: '#f8f9ff',
                                                      border: '1px solid #228be630'
                                                    }}
                                                  >
                                                    <Group justify='space-between' align='center'>
                                                      <Box>
                                                        <Text fw={500} size='sm' c='dark'>
                                                          CEA Ready
                                                        </Text>
                                                        <Text size='xs' c='dimmed'>
                                                          Mark this batch as ready for CEA
                                                          processing
                                                        </Text>
                                                      </Box>
                                                      <Switch
                                                        checked={instance.isCEAReady || false}
                                                        onChange={(event) =>
                                                          handleCEAReadyToggle(
                                                            instance.instanceId,
                                                            event.currentTarget.checked
                                                          )
                                                        }
                                                        label={
                                                          instance.isCEAReady
                                                            ? 'Ready'
                                                            : 'Not Ready'
                                                        }
                                                        color='green'
                                                        size='md'
                                                      />
                                                    </Group>
                                                  </Paper>
                                                </Box>

                                                <Box mt='md'>
                                                  {/* only appear when mode of delivery == Blended Learning */}
                                                  {instance.modeOfDelivery ===
                                                    'Blended Learning' && (
                                                    <Group
                                                      justify='space-between'
                                                      align='center'
                                                      mb='sm'
                                                    >
                                                      <Text fw={500} size='sm' c='dark.6'>
                                                        Session Breakdown (Optional)
                                                      </Text>
                                                      <Button
                                                        variant='light'
                                                        size='xs'
                                                        leftSection={<IconPlus size={12} />}
                                                        onClick={() =>
                                                          handleAddModule(instance.instanceId)
                                                        }
                                                      >
                                                        Add Session
                                                      </Button>
                                                    </Group>
                                                  )}

                                                  {instance.listModule &&
                                                    instance.listModule.length > 0 && (
                                                      <Stack gap='xs'>
                                                        {getSortedSessionsWithLabels(
                                                          instance.listModule
                                                        ).map((sessionData, _displayIndex) => {
                                                          const {
                                                            sessionLabel,
                                                            originalIndex,
                                                            ...module
                                                          } = sessionData;
                                                          return (
                                                            <Paper
                                                              key={originalIndex}
                                                              p='sm'
                                                              withBorder
                                                              style={{
                                                                backgroundColor: '#f8f9fa',
                                                                position: 'relative'
                                                              }}
                                                            >
                                                              <Group
                                                                justify='space-between'
                                                                align='center'
                                                                mb='xs'
                                                              >
                                                                <Badge
                                                                  size='md'
                                                                  variant='filled'
                                                                  color='blue'
                                                                  style={{
                                                                    fontWeight: 600,
                                                                    fontSize: '11px'
                                                                  }}
                                                                >
                                                                  {sessionLabel}
                                                                </Badge>
                                                                <Tooltip label='Remove Session'>
                                                                  <ActionIcon
                                                                    color='red'
                                                                    variant='light'
                                                                    size='sm'
                                                                    onClick={() =>
                                                                      handleRemoveModule(
                                                                        instance.instanceId,
                                                                        originalIndex
                                                                      )
                                                                    }
                                                                  >
                                                                    <IconTrash size={14} />
                                                                  </ActionIcon>
                                                                </Tooltip>
                                                              </Group>

                                                              <Stack gap='xs'>
                                                                {/* Session Type Row */}
                                                                <Box>
                                                                  <Select
                                                                    label='Session Type'
                                                                    placeholder='Select session type'
                                                                    data={getSessionModeOfDeliveryOptions(
                                                                      instance.modeOfDelivery,
                                                                      module.moduleType
                                                                    )}
                                                                    searchable
                                                                    disabled={loadingModeOfDelivery}
                                                                    value={module.moduleType}
                                                                    onChange={(value) =>
                                                                      handleModuleChange(
                                                                        instance.instanceId,
                                                                        originalIndex,
                                                                        'moduleType',
                                                                        value || ''
                                                                      )
                                                                    }
                                                                    size='xs'
                                                                    leftSection={
                                                                      <IconCalendarEvent
                                                                        size={14}
                                                                      />
                                                                    }
                                                                  />
                                                                </Box>

                                                                {/* Date and Time Row */}
                                                                <Grid gutter='xs'>
                                                                  <Grid.Col span={6}>
                                                                    <Group
                                                                      gap='xs'
                                                                      align='flex-end'
                                                                      wrap='nowrap'
                                                                    >
                                                                      <Box style={{ flex: 2 }}>
                                                                        <DateInput
                                                                          label='Start Date'
                                                                          placeholder='DD/MM/YYYY'
                                                                          value={
                                                                            module.moduleStart &&
                                                                            module.moduleStart !==
                                                                              ''
                                                                              ? (() => {
                                                                                  const date =
                                                                                    new Date(
                                                                                      module.moduleStart
                                                                                    );
                                                                                  return isNaN(
                                                                                    date.getTime()
                                                                                  )
                                                                                    ? null
                                                                                    : date;
                                                                                })()
                                                                              : null
                                                                          }
                                                                          onChange={(date) => {
                                                                            if (date) {
                                                                              const currentDate =
                                                                                module.moduleStart
                                                                                  ? new Date(
                                                                                      module.moduleStart
                                                                                    )
                                                                                  : new Date(date);
                                                                              currentDate.setFullYear(
                                                                                date.getFullYear()
                                                                              );
                                                                              currentDate.setMonth(
                                                                                date.getMonth()
                                                                              );
                                                                              currentDate.setDate(
                                                                                date.getDate()
                                                                              );
                                                                              handleModuleDateChange(
                                                                                instance.instanceId,
                                                                                originalIndex,
                                                                                'moduleStart',
                                                                                currentDate
                                                                              );
                                                                            }
                                                                            // Clear validation error when user makes a change
                                                                            if (
                                                                              validationErrors
                                                                                .sessionErrors[
                                                                                instance.instanceId
                                                                              ]?.[originalIndex]
                                                                                ?.start
                                                                            ) {
                                                                              setValidationErrors(
                                                                                (prev) => ({
                                                                                  ...prev,
                                                                                  sessionErrors: {
                                                                                    ...prev.sessionErrors,
                                                                                    [instance.instanceId]:
                                                                                      {
                                                                                        ...prev
                                                                                          .sessionErrors[
                                                                                          instance
                                                                                            .instanceId
                                                                                        ],
                                                                                        [originalIndex]:
                                                                                          {
                                                                                            ...prev
                                                                                              .sessionErrors[
                                                                                              instance
                                                                                                .instanceId
                                                                                            ]?.[
                                                                                              originalIndex
                                                                                            ],
                                                                                            start:
                                                                                              undefined
                                                                                          }
                                                                                      }
                                                                                  }
                                                                                })
                                                                              );
                                                                            }
                                                                          }}
                                                                          valueFormat='DD/MM/YYYY'
                                                                          size='xs'
                                                                          leftSection={
                                                                            <IconCalendar
                                                                              size={12}
                                                                            />
                                                                          }
                                                                          error={
                                                                            validationErrors
                                                                              .sessionErrors[
                                                                              instance.instanceId
                                                                            ]?.[originalIndex]
                                                                              ?.start
                                                                          }
                                                                          minDate={
                                                                            instance.planningStart
                                                                              ? (() => {
                                                                                  const date =
                                                                                    new Date(
                                                                                      instance.planningStart
                                                                                    );
                                                                                  return isNaN(
                                                                                    date.getTime()
                                                                                  )
                                                                                    ? undefined
                                                                                    : date;
                                                                                })()
                                                                              : undefined
                                                                          }
                                                                          maxDate={
                                                                            instance.planningEnd
                                                                              ? (() => {
                                                                                  const date =
                                                                                    new Date(
                                                                                      instance.planningEnd
                                                                                    );
                                                                                  return isNaN(
                                                                                    date.getTime()
                                                                                  )
                                                                                    ? undefined
                                                                                    : date;
                                                                                })()
                                                                              : undefined
                                                                          }
                                                                          styles={{
                                                                            label: {
                                                                              fontWeight: 500,
                                                                              fontSize: '12px'
                                                                            },
                                                                            input: {
                                                                              fontSize: '13px'
                                                                            }
                                                                          }}
                                                                        />
                                                                      </Box>
                                                                      <Box style={{ flex: 1 }}>
                                                                        <TimeInput
                                                                          label='Time'
                                                                          placeholder='HH:mm'
                                                                          value={
                                                                            module.moduleStart
                                                                              ? dayjs(
                                                                                  module.moduleStart
                                                                                ).format('HH:mm')
                                                                              : ''
                                                                          }
                                                                          onChange={(event) => {
                                                                            const timeValue =
                                                                              event.currentTarget
                                                                                .value;
                                                                            if (
                                                                              timeValue &&
                                                                              module.moduleStart
                                                                            ) {
                                                                              const [
                                                                                hours,
                                                                                minutes
                                                                              ] = timeValue
                                                                                .split(':')
                                                                                .map(Number);
                                                                              const currentDate =
                                                                                new Date(
                                                                                  module.moduleStart
                                                                                );
                                                                              currentDate.setHours(
                                                                                hours,
                                                                                minutes,
                                                                                0,
                                                                                0
                                                                              );
                                                                              handleModuleDateChange(
                                                                                instance.instanceId,
                                                                                originalIndex,
                                                                                'moduleStart',
                                                                                currentDate
                                                                              );
                                                                            }
                                                                          }}
                                                                          size='xs'
                                                                          styles={{
                                                                            label: {
                                                                              fontWeight: 500,
                                                                              fontSize: '12px'
                                                                            },
                                                                            input: {
                                                                              fontSize: '13px'
                                                                            }
                                                                          }}
                                                                        />
                                                                      </Box>
                                                                    </Group>
                                                                  </Grid.Col>
                                                                  <Grid.Col span={6}>
                                                                    <Group
                                                                      gap='xs'
                                                                      align='flex-end'
                                                                      wrap='nowrap'
                                                                    >
                                                                      <Box style={{ flex: 2 }}>
                                                                        <DateInput
                                                                          label='End Date'
                                                                          placeholder='DD/MM/YYYY'
                                                                          value={
                                                                            module.moduleEnd &&
                                                                            module.moduleEnd !== ''
                                                                              ? (() => {
                                                                                  const date =
                                                                                    new Date(
                                                                                      module.moduleEnd
                                                                                    );
                                                                                  return isNaN(
                                                                                    date.getTime()
                                                                                  )
                                                                                    ? null
                                                                                    : date;
                                                                                })()
                                                                              : null
                                                                          }
                                                                          onChange={(date) => {
                                                                            if (date) {
                                                                              const currentDate =
                                                                                module.moduleEnd
                                                                                  ? new Date(
                                                                                      module.moduleEnd
                                                                                    )
                                                                                  : new Date(date);
                                                                              currentDate.setFullYear(
                                                                                date.getFullYear()
                                                                              );
                                                                              currentDate.setMonth(
                                                                                date.getMonth()
                                                                              );
                                                                              currentDate.setDate(
                                                                                date.getDate()
                                                                              );
                                                                              handleModuleDateChange(
                                                                                instance.instanceId,
                                                                                originalIndex,
                                                                                'moduleEnd',
                                                                                currentDate
                                                                              );
                                                                            }
                                                                            // Clear validation error when user makes a change
                                                                            if (
                                                                              validationErrors
                                                                                .sessionErrors[
                                                                                instance.instanceId
                                                                              ]?.[originalIndex]
                                                                                ?.end
                                                                            ) {
                                                                              setValidationErrors(
                                                                                (prev) => ({
                                                                                  ...prev,
                                                                                  sessionErrors: {
                                                                                    ...prev.sessionErrors,
                                                                                    [instance.instanceId]:
                                                                                      {
                                                                                        ...prev
                                                                                          .sessionErrors[
                                                                                          instance
                                                                                            .instanceId
                                                                                        ],
                                                                                        [originalIndex]:
                                                                                          {
                                                                                            ...prev
                                                                                              .sessionErrors[
                                                                                              instance
                                                                                                .instanceId
                                                                                            ]?.[
                                                                                              originalIndex
                                                                                            ],
                                                                                            end: undefined
                                                                                          }
                                                                                      }
                                                                                  }
                                                                                })
                                                                              );
                                                                            }
                                                                          }}
                                                                          valueFormat='DD/MM/YYYY'
                                                                          size='xs'
                                                                          leftSection={
                                                                            <IconCalendar
                                                                              size={12}
                                                                            />
                                                                          }
                                                                          error={
                                                                            validationErrors
                                                                              .sessionErrors[
                                                                              instance.instanceId
                                                                            ]?.[originalIndex]?.end
                                                                          }
                                                                          minDate={
                                                                            instance.planningStart
                                                                              ? (() => {
                                                                                  const date =
                                                                                    new Date(
                                                                                      instance.planningStart
                                                                                    );
                                                                                  return isNaN(
                                                                                    date.getTime()
                                                                                  )
                                                                                    ? undefined
                                                                                    : date;
                                                                                })()
                                                                              : undefined
                                                                          }
                                                                          maxDate={
                                                                            instance.planningEnd
                                                                              ? (() => {
                                                                                  const date =
                                                                                    new Date(
                                                                                      instance.planningEnd
                                                                                    );
                                                                                  return isNaN(
                                                                                    date.getTime()
                                                                                  )
                                                                                    ? undefined
                                                                                    : date;
                                                                                })()
                                                                              : undefined
                                                                          }
                                                                          styles={{
                                                                            label: {
                                                                              fontWeight: 500,
                                                                              fontSize: '12px'
                                                                            },
                                                                            input: {
                                                                              fontSize: '13px'
                                                                            }
                                                                          }}
                                                                        />
                                                                      </Box>
                                                                      <Box style={{ flex: 1 }}>
                                                                        <TimeInput
                                                                          label='Time'
                                                                          placeholder='HH:mm'
                                                                          value={
                                                                            module.moduleEnd
                                                                              ? dayjs(
                                                                                  module.moduleEnd
                                                                                ).format('HH:mm')
                                                                              : ''
                                                                          }
                                                                          onChange={(event) => {
                                                                            const timeValue =
                                                                              event.currentTarget
                                                                                .value;
                                                                            if (
                                                                              timeValue &&
                                                                              module.moduleEnd
                                                                            ) {
                                                                              const [
                                                                                hours,
                                                                                minutes
                                                                              ] = timeValue
                                                                                .split(':')
                                                                                .map(Number);
                                                                              const currentDate =
                                                                                new Date(
                                                                                  module.moduleEnd
                                                                                );
                                                                              currentDate.setHours(
                                                                                hours,
                                                                                minutes,
                                                                                0,
                                                                                0
                                                                              );
                                                                              handleModuleDateChange(
                                                                                instance.instanceId,
                                                                                originalIndex,
                                                                                'moduleEnd',
                                                                                currentDate
                                                                              );
                                                                            }
                                                                          }}
                                                                          size='xs'
                                                                          styles={{
                                                                            label: {
                                                                              fontWeight: 500,
                                                                              fontSize: '12px'
                                                                            },
                                                                            input: {
                                                                              fontSize: '13px'
                                                                            }
                                                                          }}
                                                                        />
                                                                      </Box>
                                                                    </Group>
                                                                  </Grid.Col>
                                                                </Grid>
                                                              </Stack>
                                                            </Paper>
                                                          );
                                                        })}
                                                      </Stack>
                                                    )}

                                                  {instance.listModule &&
                                                    instance.listModule.length > 0 && (
                                                      <Text size='xs' c='dimmed' mt='xs'>
                                                        The main batch dates will automatically
                                                        adjust to match the earliest start and
                                                        latest end times from all modules.
                                                      </Text>
                                                    )}
                                                </Box>

                                                <Group gap='xs' mt='sm'>
                                                  <Button
                                                    variant='light'
                                                    size='xs'
                                                    leftSection={<IconPlus size={14} />}
                                                    onClick={() =>
                                                      handleDuplicateInstance(instance.instanceId)
                                                    }
                                                  >
                                                    Add batch
                                                  </Button>
                                                  <Button
                                                    variant='light'
                                                    color='red'
                                                    size='xs'
                                                    leftSection={<IconTrash size={14} />}
                                                    onClick={() =>
                                                      handleRemoveInstance(instance.instanceId)
                                                    }
                                                  >
                                                    Remove batch
                                                  </Button>
                                                </Group>
                                              </Paper>
                                            )}
                                          </Draggable>
                                        );
                                      })}
                                    </Stack>
                                  </Box>
                                </Collapse>
                              </Paper>
                            ))}
                          </Stack>
                        );
                      })()}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </Paper>
            ) : (
              <CalendarScheduleView
                courseInstances={filteredInstances}
                onDateChange={handleDateChange}
                onRemoveInstance={handleRemoveInstance}
                onUpdateInstance={handleUpdateInstance}
                conflicts={conflicts}
                onCEAReadyToggle={handleCEAReadyToggle}
                onAddModule={handleAddModule}
                onRemoveModule={handleRemoveModule}
                onModuleChange={handleModuleChange}
                onModuleDateChange={handleModuleDateChange}
                modeOfDeliveryOptions={dataModeOfDelivery?.data || []}
                loadingModeOfDelivery={loadingModeOfDelivery}
              />
            )}
          </Grid.Col>
          {viewMode === 'calendar' ? null : (
            <Grid.Col span={3}>
              <Paper p='lg' radius='md' withBorder>
                <Stack>
                  <Flex justify={'space-between'} align='center'>
                    <Title order={4}>Planning Details</Title>
                    <Badge size='md' color='blue'>
                      {schedulePlan.programType}
                    </Badge>
                  </Flex>
                  <Divider />

                  <Box>
                    <Title order={5} mb='xs'>
                      Timeline
                    </Title>
                    <Grid>
                      <Grid.Col span={6}>
                        <Text size='sm' color='dimmed'>
                          Schedule Start Time
                        </Text>
                        <Text>{formatDateTimeForDisplay(schedulePlan.planningStart)}</Text>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Text size='sm' color='dimmed'>
                          Schedule End Time
                        </Text>
                        <Text>{formatDateTimeForDisplay(schedulePlan.planningEnd)}</Text>
                      </Grid.Col>
                    </Grid>
                  </Box>
                  <Box mt='md'>
                    <Title order={5} mb='xs'>
                      Target Positions
                    </Title>
                    <Stack gap={4}>
                      {schedulePlan.targetPositions.map((position, index) => (
                        <Text key={position.trLCMSPlanningTargetPositionsID} size='sm' fw={500}>
                          {index + 1}. {position.positionName}
                        </Text>
                      ))}
                    </Stack>
                  </Box>
                  <Divider mt='md' />
                  <Box>
                    <Text size='sm' color='dimmed'>
                      Total Budget (temp)
                    </Text>
                    <Badge variant='outline' color='violet'>
                      {new Intl.NumberFormat('id-ID', {
                        style: 'currency',
                        currency: 'IDR',
                        maximumFractionDigits: 0
                      }).format(totalBudgetTemp)}
                    </Badge>
                  </Box>
                  <Divider mt='md' />
                  <Box>
                    <Stack gap='xs'>
                      {/* Assessment Start Date and Time */}
                      <Box>
                        <Text size='sm' color='dimmed' mb='xs'>
                          Assessment Schedule Start
                        </Text>
                        <Group gap='xs' align='flex-end'>
                          <Box style={{ flex: 2 }}>
                            <DateInput
                              placeholder='DD/MM/YYYY'
                              value={assessmentStartDate}
                              onChange={(date) => {
                                if (date) {
                                  // Preserve existing time or default to 00:00
                                  const currentDate = assessmentStartDate
                                    ? new Date(assessmentStartDate)
                                    : new Date(date);
                                  currentDate.setFullYear(date.getFullYear());
                                  currentDate.setMonth(date.getMonth());
                                  currentDate.setDate(date.getDate());
                                  setAssessmentStartDate(currentDate);
                                  setHasChanges(true);
                                } else {
                                  setAssessmentStartDate(null);
                                  setHasChanges(true);
                                }
                                // Clear validation error when user makes a change
                                if (validationErrors.assessmentErrors.start) {
                                  setValidationErrors((prev) => ({
                                    ...prev,
                                    assessmentErrors: {
                                      ...prev.assessmentErrors,
                                      start: undefined
                                    }
                                  }));
                                }
                              }}
                              valueFormat='DD/MM/YYYY'
                              size='sm'
                              leftSection={<IconCalendar size={14} />}
                              popoverProps={{
                                position: 'bottom',
                                withinPortal: true
                              }}
                              error={validationErrors.assessmentErrors.start}
                            />
                          </Box>
                          <Box style={{ flex: 1 }}>
                            <TimeInput
                              placeholder='HH:mm'
                              value={
                                assessmentStartDate
                                  ? dayjs(assessmentStartDate).format('HH:mm')
                                  : ''
                              }
                              onChange={(event) => {
                                const timeValue = event.currentTarget.value;
                                if (timeValue && assessmentStartDate) {
                                  const [hours, minutes] = timeValue.split(':').map(Number);
                                  const currentDate = new Date(assessmentStartDate);
                                  currentDate.setHours(hours, minutes, 0, 0);
                                  setAssessmentStartDate(currentDate);
                                  setHasChanges(true);
                                }
                              }}
                              size='sm'
                            />
                          </Box>
                        </Group>
                      </Box>

                      {/* Assessment End Date and Time */}
                      <Box>
                        <Text size='sm' color='dimmed' mb='xs'>
                          Assessment Schedule End
                        </Text>
                        <Group gap='xs' align='flex-end'>
                          <Box style={{ flex: 2 }}>
                            <DateInput
                              placeholder='DD/MM/YYYY'
                              value={assessmentEndDate}
                              onChange={(date) => {
                                if (date) {
                                  // Preserve existing time or default to 23:59
                                  const currentDate = assessmentEndDate
                                    ? new Date(assessmentEndDate)
                                    : new Date(date);
                                  currentDate.setFullYear(date.getFullYear());
                                  currentDate.setMonth(date.getMonth());
                                  currentDate.setDate(date.getDate());
                                  setAssessmentEndDate(currentDate);
                                  setHasChanges(true);
                                } else {
                                  setAssessmentEndDate(null);
                                  setHasChanges(true);
                                }
                                // Clear validation error when user makes a change
                                if (validationErrors.assessmentErrors.end) {
                                  setValidationErrors((prev) => ({
                                    ...prev,
                                    assessmentErrors: {
                                      ...prev.assessmentErrors,
                                      end: undefined
                                    }
                                  }));
                                }
                              }}
                              valueFormat='DD/MM/YYYY'
                              size='sm'
                              leftSection={<IconCalendar size={14} />}
                              popoverProps={{
                                position: 'bottom',
                                withinPortal: true
                              }}
                              error={validationErrors.assessmentErrors.end}
                            />
                          </Box>
                          <Box style={{ flex: 1 }}>
                            <TimeInput
                              placeholder='HH:mm'
                              value={
                                assessmentEndDate ? dayjs(assessmentEndDate).format('HH:mm') : ''
                              }
                              onChange={(event) => {
                                const timeValue = event.currentTarget.value;
                                if (timeValue && assessmentEndDate) {
                                  const [hours, minutes] = timeValue.split(':').map(Number);
                                  const currentDate = new Date(assessmentEndDate);
                                  currentDate.setHours(hours, minutes, 0, 0);
                                  setAssessmentEndDate(currentDate);
                                  setHasChanges(true);
                                }
                              }}
                              size='sm'
                            />
                          </Box>
                        </Group>
                      </Box>
                    </Stack>
                  </Box>

                  <Divider mt='md' />
                  <Box mt='md'>
                    <Title order={5} mb='xs'>
                      Summary
                    </Title>
                    <Group gap='xs'>
                      <Badge variant='outline' size='sm'>
                        {courseInstances.length} batches
                      </Badge>
                      <Badge variant='outline' size='sm'>
                        {schedulePlan.targetPositions.length} positions
                      </Badge>
                      {conflicts.length > 0 && (
                        <Badge variant='filled' color='red' size='sm'>
                          {conflicts.length} conflicts
                        </Badge>
                      )}
                    </Group>
                  </Box>
                  <Divider mt='md' />

                  {/* Action Buttons */}
                  <Stack mt='md'>
                    {hasChanges && (
                      <Button
                        fullWidth
                        color='primary'
                        onClick={handleSave}
                        leftSection={<IconDeviceFloppy size={16} />}
                        loading={updateMutation.isPending}
                      >
                        Save Changes
                      </Button>
                    )}

                    <Button
                      fullWidth
                      variant='subtle'
                      color='gray'
                      onClick={handleBack}
                      leftSection={<IconArrowLeft size={16} />}
                    >
                      Go Back
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Grid.Col>
          )}
        </Grid>
      </Stack>
      <ScheduleImportExportModal
        opened={scheduleModalOpened}
        onClose={() => setScheduleModalOpened(false)}
        courseInstances={courseInstances}
        onImport={(newInstances) => {
          setCourseInstances((prev) => [...prev, ...newInstances]);
          setHasChanges(true);
        }}
        schedulePlanName={schedulePlan?.planningName || 'Schedule Plan'}
        availableCourses={Array.from(
          new Map(
            courseInstances.map((instance) => [
              instance.msLCMSCourseID,
              {
                instanceId: instance.instanceId,
                msLCMSCourseID: instance.msLCMSCourseID,
                courseName: instance.courseName,
                courseCode: instance.courseCode,
                learningHours: instance.learningHours,
                deliveryType: instance.deliveryType,
                planningStart: '',
                planningEnd: '',
                trLCMSPlanningCoursesID: instance.trLCMSPlanningCoursesID
              }
            ])
          ).values()
        )}
      />
      <FacilitatorsModal
        opened={facilitatorsModalOpened}
        onClose={() => setFacilitatorsModalOpened(false)}
        facilitators={selectedFacilitators}
        courseName={selectedCourseName}
      />
    </DragDropContext>
  );
};

export default SchedulePlanEditPage;
